// ─────────────────────────────────────────────────────────────────
// Control Systems — Cascade & Feedforward Control
// ─────────────────────────────────────────────────────────────────

const S = {
  wrapper:      { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c0392b', paddingBottom:'8px', marginBottom:'24px', color:'#7B0D0D' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#c0392b', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c0392b', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#FEF0F0', border:'2px solid #c0392b', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#7B0D0D', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c0392b' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#c0392b', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff5f5' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff' },
  tdL:          { fontWeight:'600', color:'#7B0D0D', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#fffafa', border:'1px solid #f0cece', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#7B0D0D', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Explanation / Formula</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0 ? S.tdE : S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0 ? S.tdE : S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Arr({ id, color }) {
  return (
    <marker id={id} markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
      <polygon points="0 0,7 3,0 6" fill={color}/>
    </marker>
  );
}

// ─────────────────────────────────────────────────────────────────
// Shared simulation helpers — Euler integration
// ─────────────────────────────────────────────────────────────────

// Outer plant: G2(s)=1/((s+1)(2s+1))  Inner plant: G1(s)=1/(0.2s+1)
function simSingleLoop(Kp, Ki, Kd, tMax, dt, disturbFn) {
  const N = Math.round(tMax / dt);
  const pts = [];
  let x1=0, x2=0, integral=0, prevErr=0;
  for (let i=0; i<=N; i++) {
    const t = i*dt;
    const r = 1;
    const d = disturbFn ? disturbFn(t) : 0;
    const y = x2;
    const err = r - y;
    integral += err * dt;
    const deriv = (err - prevErr) / dt;
    const u = Math.max(-15, Math.min(15, Kp*err + Ki*integral + Kd*deriv));
    pts.push({ t, y, u, err });
    prevErr = err;
    // outer plant: 1/((s+1)(2s+1))
    const dx1 = (-x1 + u + d);
    const dx2 = (-x2/2 + x1/2);
    x1 += dx1*dt; x2 += dx2*dt;
  }
  return pts;
}

function simCascade(KpOuter, KiOuter, KdOuter, KpInner, KiInner, tMax, dt, disturbFn) {
  const N = Math.round(tMax / dt);
  const pts = [];
  // outer plant state (slow): dx2 = -x2/2 + x1/2
  // inner plant state (fast): dx1 = -5*x1 + 5*u
  let x1=0, x2=0;
  let iOuter=0, iInner=0;
  let prevErrOuter=0;
  for (let i=0; i<=N; i++) {
    const t = i*dt;
    const r = 1;
    const d = disturbFn ? disturbFn(t) : 0;
    const yOuter = x2;
    const yInner = x1;

    // Outer controller
    const errOuter = r - yOuter;
    iOuter += errOuter * dt;
    const derivOuter = (errOuter - prevErrOuter) / dt;
    const rInner = Math.max(-8, Math.min(8, KpOuter*errOuter + KiOuter*iOuter + KdOuter*derivOuter));

    // Inner controller
    const errInner = rInner - yInner;
    iInner += errInner * dt;
    const uInner = Math.max(-15, Math.min(15, KpInner*errInner + KiInner*iInner));

    pts.push({ t, y: yOuter, u: uInner, yInner, rInner });
    prevErrOuter = errOuter;

    // Inner plant: G1(s)=1/(0.2s+1)  →  dx1 = -5x1 + 5*u + disturbance
    const dx1 = -5*x1 + 5*(uInner + d);
    // Outer plant: 1/(2s+1) driven by inner output
    const dx2 = -x2/2 + x1/2;
    x1 += dx1*dt; x2 += dx2*dt;
  }
  return pts;
}

// Feedforward simulation
function simFeedforward(Kp, Ki, ffGain, tMax, dt, disturbFn, ffDisturbGain) {
  const N = Math.round(tMax / dt);
  const pts = [];
  let x1=0, x2=0, integral=0, prevErr=0;
  for (let i=0; i<=N; i++) {
    const t = i*dt;
    const r = 1;
    const d = disturbFn ? disturbFn(t) : 0;
    const y = x2;
    const err = r - y;
    integral += err * dt;
    const deriv = (err - prevErr) / dt;
    const ufb = Kp*err + Ki*integral + Kp*0.3*deriv;
    const uff_r = ffGain * r;             // reference FF
    const uff_d = ffDisturbGain ? ffDisturbGain * d : 0; // disturbance FF
    const u = Math.max(-15, Math.min(15, ufb + uff_r + uff_d));
    pts.push({ t, y, u, err });
    prevErr = err;
    const dx1 = -x1 + u + d;
    const dx2 = -x2/2 + x1/2;
    x1 += dx1*dt; x2 += dx2*dt;
  }
  return pts;
}

function mkPath(pts, sx, sy, key) {
  return pts.map((p, i) =>
    `${i===0?'M':'L'}${sx(p.t).toFixed(1)},${sy(p[key]).toFixed(1)}`
  ).join(' ');
}

// ─────────────────────────────────────────────────────────────────
// Diagram 1 — Cascade Control Block Diagram
// ─────────────────────────────────────────────────────────────────
function DiagramCascadeStructure() {
  const W = 900, H = 400;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Cascade Control — Two-Loop Architecture &amp; Signal Flow</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <Arr id="cs-blk" color="#555"/>
          <Arr id="cs-red" color="#c0392b"/>
          <Arr id="cs-blu" color="#2E75B6"/>
          <Arr id="cs-grn" color="#27ae60"/>
          <Arr id="cs-orn" color="#e07b00"/>
          <Arr id="cs-pur" color="#9b59b6"/>
        </defs>

        {/* ── MAIN DIAGRAM ── */}
        <rect x={10} y={10} width={880} height={230} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={450} y={28} textAnchor="middle" fontSize={13} fill="#7B0D0D" fontWeight="700">Cascade (Two-Loop) Control Block Diagram</text>

        {/* Outer loop shading */}
        <rect x={14} y={34} width={872} height={200} rx={4} fill="#eaf3ff" opacity="0.25"/>
        {/* Inner loop shading */}
        <rect x={290} y={42} width={490} height={130} rx={4} fill="#f0fff4" opacity="0.4"/>

        {/* Loop labels */}
        <text x={22} y={48} fontSize={9.5} fill="#2E75B6" fontWeight="700" fontStyle="italic">Outer (primary) loop</text>
        <text x={298} y={56} fontSize={9}   fill="#27ae60" fontWeight="700" fontStyle="italic">Inner (secondary) loop</text>

        {/* R(s) — outer setpoint */}
        <text x={18} y={135} fontSize={11} fill="#c0392b" fontWeight="700">R(s)</text>
        <line x1={44} y1={131} x2={80} y2={131} stroke="#c0392b" strokeWidth="2" markerEnd="url(#cs-red)"/>

        {/* Outer summing junction */}
        <circle cx={98} cy={131} r={17} fill="#FEF0F0" stroke="#c0392b" strokeWidth="2"/>
        <text x={98}  y={136} textAnchor="middle" fontSize={15} fill="#c0392b" fontWeight="700">Σ</text>
        <text x={82}  y={127} fontSize={10} fill="#27ae60" fontWeight="700">+</text>
        <text x={94}  y={148} fontSize={10} fill="#c0392b" fontWeight="700">−</text>
        <line x1={115} y1={131} x2={148} y2={131} stroke="#555" strokeWidth="2" markerEnd="url(#cs-blk)"/>
        <text x={118} y={124} fontSize={9}  fill="#c0392b">e₁(s)</text>

        {/* Outer controller C1 */}
        <rect x={148} y={113} width={86} height={36} rx={4} fill="#FEF0F0" stroke="#c0392b" strokeWidth="2"/>
        <text x={191} y={129} textAnchor="middle" fontSize={12} fill="#c0392b" fontWeight="700">C₁(s)</text>
        <text x={191} y={143} textAnchor="middle" fontSize={9}  fill="#7B0D0D">Outer ctrl</text>
        <line x1={234} y1={131} x2={280} y2={131} stroke="#555" strokeWidth="2" markerEnd="url(#cs-blk)"/>
        <text x={240} y={124} fontSize={9} fill="#555">r₂(s)</text>
        <text x={240} y={145} fontSize={8} fill="#555">(inner SP)</text>

        {/* Inner summing junction */}
        <circle cx={298} cy={131} r={17} fill="#f0fff4" stroke="#27ae60" strokeWidth="2"/>
        <text x={298} y={136} textAnchor="middle" fontSize={15} fill="#27ae60" fontWeight="700">Σ</text>
        <text x={282} y={127} fontSize={10} fill="#27ae60" fontWeight="700">+</text>
        <text x={294} y={148} fontSize={10} fill="#27ae60" fontWeight="700">−</text>
        <line x1={315} y1={131} x2={352} y2={131} stroke="#555" strokeWidth="2" markerEnd="url(#cs-blk)"/>
        <text x={318} y={124} fontSize={9} fill="#27ae60">e₂(s)</text>

        {/* Inner controller C2 */}
        <rect x={352} y={113} width={86} height={36} rx={4} fill="#f0fff4" stroke="#27ae60" strokeWidth="2"/>
        <text x={395} y={129} textAnchor="middle" fontSize={12} fill="#27ae60" fontWeight="700">C₂(s)</text>
        <text x={395} y={143} textAnchor="middle" fontSize={9}  fill="#27ae60">Inner ctrl</text>
        <line x1={438} y1={131} x2={478} y2={131} stroke="#555" strokeWidth="2" markerEnd="url(#cs-blk)"/>
        <text x={444} y={124} fontSize={9} fill="#555">u(s)</text>

        {/* Inner disturbance D₂ */}
        <line x1={510} y1={68} x2={510} y2={114} stroke="#e07b00" strokeWidth="2" strokeDasharray="5,3" markerEnd="url(#cs-orn)"/>
        <text x={518} y={63} fontSize={10} fill="#e07b00" fontWeight="700">D₂(s)</text>
        <text x={518} y={76} fontSize={8.5} fill="#555">inner dist.</text>

        {/* Inner summing for disturbance */}
        <circle cx={510} cy={131} r={14} fill="#fff5e6" stroke="#e07b00" strokeWidth="1.5"/>
        <text x={510} y={136} textAnchor="middle" fontSize={12} fill="#e07b00" fontWeight="700">Σ</text>
        <line x1={478} y1={131} x2={492} y2={131} stroke="#555" strokeWidth="2" markerEnd="url(#cs-blk)"/>
        <line x1={524} y1={131} x2={556} y2={131} stroke="#555" strokeWidth="2" markerEnd="url(#cs-blk)"/>

        {/* Inner plant G2 */}
        <rect x={556} y={113} width={86} height={36} rx={4} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="2"/>
        <text x={599} y={129} textAnchor="middle" fontSize={12} fill="#2E75B6" fontWeight="700">G₂(s)</text>
        <text x={599} y={143} textAnchor="middle" fontSize={9}  fill="#2E75B6">inner plant</text>

        {/* y₂ pick-off */}
        <line x1={642} y1={131} x2={700} y2={131} stroke="#555" strokeWidth="2"/>
        <circle cx={680} cy={131} r={4} fill="#555"/>
        <text x={648} y={124} fontSize={9} fill="#27ae60">y₂(s)</text>

        {/* Outer disturbance D₁ */}
        <line x1={710} y1={68} x2={710} y2={114} stroke="#9b59b6" strokeWidth="2" strokeDasharray="5,3" markerEnd="url(#cs-pur)"/>
        <text x={718} y={63} fontSize={10} fill="#9b59b6" fontWeight="700">D₁(s)</text>
        <text x={718} y={76} fontSize={8.5} fill="#555">outer dist.</text>

        {/* Outer disturbance summing */}
        <circle cx={710} cy={131} r={14} fill="#f5d0ff" stroke="#9b59b6" strokeWidth="1.5"/>
        <text x={710} y={136} textAnchor="middle" fontSize={12} fill="#9b59b6" fontWeight="700">Σ</text>
        <line x1={700} y1={131} x2={692} y2={131} stroke="#555" strokeWidth="2" markerEnd="url(#cs-blk)"/>
        <line x1={724} y1={131} x2={756} y2={131} stroke="#555" strokeWidth="2" markerEnd="url(#cs-blk)"/>

        {/* Outer plant G1 */}
        <rect x={756} y={113} width={86} height={36} rx={4} fill="#f5d0ff" stroke="#9b59b6" strokeWidth="2"/>
        <text x={799} y={129} textAnchor="middle" fontSize={12} fill="#9b59b6" fontWeight="700">G₁(s)</text>
        <text x={799} y={143} textAnchor="middle" fontSize={9}  fill="#9b59b6">outer plant</text>

        {/* y₁ output */}
        <line x1={842} y1={131} x2={882} y2={131} stroke="#27ae60" strokeWidth="2" markerEnd="url(#cs-grn)"/>
        <circle cx={860} cy={131} r={4} fill="#27ae60"/>
        <text x={852} y={124} fontSize={10} fill="#27ae60" fontWeight="700">y₁(s)</text>
        <text x={866} y={144} fontSize={9}  fill="#27ae60">Output</text>

        {/* Inner feedback loop */}
        <line x1={680} y1={135} x2={680} y2={182} stroke="#27ae60" strokeWidth="1.5"/>
        <line x1={680} y1={182} x2={298} y2={182} stroke="#27ae60" strokeWidth="1.5"/>
        <line x1={298} y1={182} x2={298} y2={148} stroke="#27ae60" strokeWidth="1.5" markerEnd="url(#cs-grn)"/>
        <text x={488} y={195} textAnchor="middle" fontSize={9} fill="#27ae60" fontStyle="italic">Inner feedback: y₂(s) measured by inner sensor</text>

        {/* Outer feedback loop */}
        <line x1={860} y1={135} x2={860} y2={210} stroke="#2E75B6" strokeWidth="1.5"/>
        <line x1={860} y1={210} x2={98}  y2={210} stroke="#2E75B6" strokeWidth="1.5"/>
        <line x1={98}  y1={210} x2={98}  y2={148} stroke="#2E75B6" strokeWidth="1.5" markerEnd="url(#cs-blu)"/>
        <text x={480} y={222} textAnchor="middle" fontSize={9} fill="#2E75B6" fontStyle="italic">Outer feedback: y₁(s) measured by outer sensor</text>

        {/* ── LOWER: Properties panels ── */}
        <rect x={10} y={252} width={880} height={140} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={450} y={270} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Key Properties &amp; Design Rules</text>

        {[
          { x:18,  color:'#c0392b', bg:'#FEF0F0',
            title:'Outer Loop (Primary)',
            items:['Controls the main process variable y₁',
                   'Setpoint R(s) from operator',
                   'Output r₂ = setpoint for inner loop',
                   'Slower — uses full process dynamics',
                   'C₁: typically PI or PID'] },
          { x:234, color:'#27ae60', bg:'#f0fff4',
            title:'Inner Loop (Secondary)',
            items:['Controls fast/intermediate variable y₂',
                   'Setpoint r₂ from outer controller',
                   'Must be 3-10× faster than outer loop',
                   'Rejects inner disturbances D₂ quickly',
                   'C₂: typically P or PI'] },
          { x:450, color:'#e07b00', bg:'#fff5e6',
            title:'Inner Disturbance D₂',
            items:['Enters between actuator and inner plant',
                   'Corrected by inner loop before reaching y₁',
                   'Key advantage: faster rejection than single loop',
                   'Inner loop bandwidth >> outer loop bandwidth',
                   'Examples: supply pressure, actuator friction'] },
          { x:666, color:'#9b59b6', bg:'#f5d0ff',
            title:'Design Requirements',
            items:['Inner loop must be stable independently',
                   'Inner BW ≥ 3–10× outer BW (rule of thumb)',
                   'Tune inner loop first, then outer loop',
                   'Inner variable y₂ must be measurable',
                   'Inner variable must correlate with u→y₁ path'] },
        ].map(({ x, color, bg, title, items }) => (
          <g key={x}>
            <rect x={x+2} y={278} width={210} height={108} rx={3} fill={bg} stroke={color} strokeWidth="1.2"/>
            <text x={x+10} y={293} fontSize={10} fill={color} fontWeight="700">{title}</text>
            {items.map((item, j) => (
              <text key={j} x={x+10} y={307+j*16} fontSize={8.5} fill="#333">▸ {item}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Cascade control nests an inner (secondary) feedback loop inside an outer (primary) loop. The outer controller C₁ receives the process setpoint R and outputs an inner setpoint r₂. The inner controller C₂ drives the actuator to track r₂. Inner disturbances D₂ (entering before the inner plant) are corrected by the fast inner loop before they propagate to y₁ — the key performance advantage over a single feedback loop.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 2 — Cascade vs Single-Loop: Disturbance Rejection
// ─────────────────────────────────────────────────────────────────
function DiagramCascadeVsSingle() {
  const W = 900, H = 360;
  const dt=0.02, tMax=20;

  // Disturbance: step at t=5, removed at t=12
  const dist = (t) => (t>=5 && t<12) ? 0.8 : 0;

  // Single loop PI — tuned for the combined plant
  const singlePts   = simSingleLoop(3, 0.8, 0.4, tMax, dt, dist);
  const singleNoPts = simSingleLoop(3, 0.8, 0.4, tMax, dt, null); // no disturbance

  // Cascade: inner P-only, outer PI
  const cascadePts   = simCascade(2, 0.6, 0.2, 8, 1, tMax, dt, dist);
  const cascadeNoPts = simCascade(2, 0.6, 0.2, 8, 1, tMax, dt, null);

  const ox=20, oy=24, PW=870, PH=280;
  const sx = t  => ox+36+(t/tMax)*(PW-52);
  const sy = yv => oy+PH-22-Math.max(Math.min(yv,1.5),-0.3)*(PH-36)/1.8;

  const singlePath  = mkPath(singlePts,   sx, sy, 'y');
  const cascPath    = mkPath(cascadePts,  sx, sy, 'y');
  const refPath     = singlePts.map(({t},i) => `${i===0?'M':'L'}${sx(t).toFixed(1)},${sy(1).toFixed(1)}`).join(' ');
  const distPath    = singlePts.map(({t},i) => `${i===0?'M':'L'}${sx(t).toFixed(1)},${(oy+PH-8+dist(t)*-18).toFixed(1)}`).join(' ');

  // Compute peak deviations
  let singlePeak=0, cascPeak=0;
  for (let i=0; i<singlePts.length; i++) {
    const sd = Math.abs(singlePts[i].y - 1);
    const cd = Math.abs(cascadePts[i].y - 1);
    if (sd > singlePeak && singlePts[i].t > 5) singlePeak = sd;
    if (cd > cascPeak   && cascadePts[i].t > 5) cascPeak  = cd;
  }

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Cascade vs Single-Loop — Disturbance Rejection Comparison (Computed)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <Arr id="cvs-blk" color="#aaa"/>
          <clipPath id="cvs-clip"><rect x={56} y={26} width={PW-50} height={PH-20}/></clipPath>
        </defs>

        <rect x={ox} y={oy} width={PW} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>

        {/* Grid */}
        {[2,4,6,8,10,12,14,16,18,20].map(tv=>(
          <line key={tv} x1={sx(tv)} y1={oy+6} x2={sx(tv)} y2={oy+PH-20} stroke="#f8f8f8" strokeWidth="1"/>
        ))}
        {[0.2,0.4,0.6,0.8,1.0,1.2].map(yv=>(
          <g key={yv}>
            <line x1={ox+34} y1={sy(yv)} x2={ox+PW-16} y2={sy(yv)} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={ox+30} y={sy(yv)+3} textAnchor="end" fontSize={8.5} fill="#bbb">{yv.toFixed(1)}</text>
          </g>
        ))}

        {/* Reference line */}
        <line x1={ox+34} y1={sy(1)} x2={ox+PW-16} y2={sy(1)} stroke="#27ae60" strokeWidth="1.5" strokeDasharray="7,5" opacity="0.7"/>
        <text x={ox+PW-12} y={sy(1)-4} fontSize={9} fill="#27ae60" fontWeight="700">r=1</text>

        {/* Disturbance shading */}
        <rect x={sx(5)} y={oy+6} width={sx(12)-sx(5)} height={PH-24} fill="#e07b00" opacity="0.05"/>
        <line x1={sx(5)}  y1={oy+6} x2={sx(5)}  y2={oy+PH-20} stroke="#e07b00" strokeWidth="1.2" strokeDasharray="4,3" opacity="0.6"/>
        <line x1={sx(12)} y1={oy+6} x2={sx(12)} y2={oy+PH-20} stroke="#e07b00" strokeWidth="1.2" strokeDasharray="4,3" opacity="0.6"/>
        <text x={(sx(5)+sx(12))/2} y={oy+18} textAnchor="middle" fontSize={9} fill="#e07b00" fontWeight="700">D₂ active (step disturbance = 0.8)</text>

        {/* Axes */}
        <line x1={ox+34} y1={oy+PH-20} x2={ox+PW-8}  y2={oy+PH-20} stroke="#aaa" strokeWidth="1.3" markerEnd="url(#cvs-blk)"/>
        <line x1={ox+34} y1={oy+PH-20} x2={ox+34}    y2={oy+4}     stroke="#aaa" strokeWidth="1.3" markerEnd="url(#cvs-blk)"/>
        <text x={ox+PW-4} y={oy+PH-16} fontSize={8.5} fill="#aaa">t (s)</text>
        <text x={ox+38}   y={oy+6}      fontSize={8.5} fill="#aaa">y₁(t)</text>

        {/* Curves */}
        <path d={refPath}    fill="none" stroke="#27ae60" strokeWidth="1.5" strokeDasharray="7,5" clipPath="url(#cvs-clip)"/>
        <path d={singlePath} fill="none" stroke="#c0392b" strokeWidth="2.5" clipPath="url(#cvs-clip)"/>
        <path d={cascPath}   fill="none" stroke="#2E75B6" strokeWidth="2.5" clipPath="url(#cvs-clip)"/>

        {/* Peak deviation annotations */}
        <text x={sx(8)} y={sy(1-singlePeak-0.04)} fontSize={9} fill="#c0392b" fontWeight="700">
          Single-loop peak: {(singlePeak*100).toFixed(1)}% dev.
        </text>
        <text x={sx(7.5)} y={sy(1-cascPeak*0.5-0.04)} fontSize={9} fill="#2E75B6" fontWeight="700">
          Cascade peak: {(cascPeak*100).toFixed(1)}% dev.
        </text>

        {/* Legend */}
        <rect x={ox+36} y={oy+PH-18} width={PW-52} height={14} rx={2} fill="#fffcf5"/>
        {[
          {c:'#c0392b',lbl:'Single-loop PI (Kp=3, Ki=0.8)'},
          {c:'#2E75B6', lbl:'Cascade (inner P Kp=8, outer PI Kp=2, Ki=0.6)'},
          {c:'#27ae60', lbl:'Reference r=1'},
        ].map(({c,lbl},i) => (
          <g key={i}>
            <line x1={ox+44+i*260} y1={oy+PH-11} x2={ox+62+i*260} y2={oy+PH-11} stroke={c} strokeWidth="2"/>
            <text x={ox+66+i*260} y={oy+PH-7} fontSize={8.5} fill={c}>{lbl}</text>
          </g>
        ))}

        {/* Bottom improvement box */}
        <rect x={ox+34} y={oy+PH+6} width={PW-52} height={48} rx={4} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.2"/>
        <text x={ox+PW/2} y={oy+PH+20} textAnchor="middle" fontSize={10.5} fill="#7B0D0D" fontWeight="700">
          Why Cascade Is Better at Disturbance Rejection
        </text>
        <text x={ox+PW/2} y={oy+PH+35} textAnchor="middle" fontSize={9.5} fill="#333">
          Inner loop bandwidth ≈ 5× outer loop → D₂ corrected within inner loop before it affects y₁
        </text>
        <text x={ox+PW/2} y={oy+PH+49} textAnchor="middle" fontSize={9.5} fill="#555">
          Single loop: outer PI must wait for y₁ to deviate before responding — slow, larger excursion
        </text>
      </svg>
      <div style={S.dCaption}>With an inner disturbance D₂ active from t=5 to t=12, cascade control (blue) shows a dramatically smaller deviation from the setpoint than single-loop PI (red). The inner loop detects and corrects the disturbance via y₂ before it fully propagates through the outer plant G₁ to affect y₁. Single-loop control can only react after y₁ has already deviated — by which time the disturbance has passed through the entire plant.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 3 — Inner-Loop Bandwidth Effect on Outer-Loop Response
// ─────────────────────────────────────────────────────────────────
function DiagramInnerLoopEffect() {
  const W = 900, H = 360;
  const dt=0.02, tMax=20;

  // Show how inner-loop gain affects outer-loop step response
  const configs = [
    { KpInner:2,  label:'KpInner=2  (slow inner)',  color:'#c0392b' },
    { KpInner:6,  label:'KpInner=6  (med inner)',   color:'#e07b00' },
    { KpInner:12, label:'KpInner=12 (fast inner)',  color:'#27ae60' },
    { KpInner:25, label:'KpInner=25 (very fast)',   color:'#2E75B6' },
  ];

  // For each, run cascade with same outer PI and no disturbance
  const allPts = configs.map(({ KpInner, color, label }) => {
    const pts = simCascade(2, 0.6, 0.15, KpInner, 1, tMax, dt, null);
    return { pts, color, label, KpInner };
  });

  // Also run single-loop PI for baseline
  const singlePts = simSingleLoop(2, 0.5, 0.2, tMax, dt, null);

  const PW=430, PH=250;
  const ox=20, oy=24;
  const sx = t  => ox+34+(t/tMax)*(PW-50);
  const sy = yv => oy+PH-22-Math.max(Math.min(yv,1.6),-0.1)*(PH-38)/1.7;

  // Inner-loop closed-loop poles vs KpInner
  // G_inner_CL = KpInner*(1/(0.2s+1)) / (1 + KpInner/(0.2s+1))
  //            = KpInner / (0.2s + 1 + KpInner) → pole at s = -(1+KpInner)/0.2
  const innerBWs = configs.map(({ KpInner }) => (1 + KpInner) / 0.2);

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Inner-Loop Bandwidth Effect — How Inner Gain Shapes Outer Response</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <Arr id="ile-blk" color="#aaa"/>
          <clipPath id="ile-clip"><rect x={54} y={26} width={PW-48} height={PH-20}/></clipPath>
        </defs>

        {/* Step response plot */}
        <rect x={ox} y={oy} width={PW} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={ox+PW/2} y={oy-8} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Outer-Loop Step Response — Varying Inner Kp</text>

        {[5,10,15,20].map(tv=>(
          <line key={tv} x1={sx(tv)} y1={oy+6} x2={sx(tv)} y2={oy+PH-20} stroke="#f8f8f8" strokeWidth="1"/>
        ))}
        {[0.5,1.0,1.5].map(yv=>(
          <g key={yv}>
            <line x1={ox+32} y1={sy(yv)} x2={ox+PW-14} y2={sy(yv)} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={ox+28} y={sy(yv)+3} textAnchor="end" fontSize={8.5} fill="#bbb">{yv.toFixed(1)}</text>
          </g>
        ))}

        <line x1={ox+32} y1={sy(1)} x2={ox+PW-14} y2={sy(1)} stroke="#27ae60" strokeWidth="1.3" strokeDasharray="6,4" opacity="0.6"/>
        <line x1={ox+32} y1={oy+PH-20} x2={ox+PW-8} y2={oy+PH-20} stroke="#aaa" strokeWidth="1.3" markerEnd="url(#ile-blk)"/>
        <line x1={ox+32} y1={oy+PH-20} x2={ox+32}   y2={oy+4}     stroke="#aaa" strokeWidth="1.3" markerEnd="url(#ile-blk)"/>
        <text x={ox+PW-4} y={oy+PH-16} fontSize={8.5} fill="#aaa">t</text>

        {/* Single loop baseline */}
        <path d={mkPath(singlePts, sx, sy, 'y')} fill="none" stroke="#aaa" strokeWidth="1.5" strokeDasharray="8,4" clipPath="url(#ile-clip)"/>

        {/* Cascade curves */}
        {allPts.map(({ pts, color }, i) => (
          <path key={i} d={mkPath(pts, sx, sy, 'y')} fill="none" stroke={color} strokeWidth="2" clipPath="url(#ile-clip)"/>
        ))}

        {/* Legend */}
        <line x1={ox+36} y1={oy+PH-12} x2={ox+54} y2={oy+PH-12} stroke="#aaa" strokeWidth="1.5" strokeDasharray="6,3"/>
        <text x={ox+58} y={oy+PH-8} fontSize={8.5} fill="#aaa">Single-loop baseline</text>
        {allPts.map(({ color, label }, i) => (
          <g key={i}>
            <line x1={ox+36+i*110} y1={oy+PH+4} x2={ox+54+i*110} y2={oy+PH+4} stroke={color} strokeWidth="2"/>
            <text x={ox+58+i*110} y={oy+PH+8} fontSize={8} fill={color}>{label}</text>
          </g>
        ))}

        {/* ── RIGHT: Inner-loop analysis ── */}
        <rect x={468} y={oy} width={422} height={PH+90} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={679} y={oy+18} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Inner-Loop Closed-Loop Analysis</text>

        {/* CL TF derivation */}
        <rect x={476} y={oy+26} width={406} height={52} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1.2"/>
        <text x={679} y={oy+40} textAnchor="middle" fontSize={10} fill="#27ae60" fontWeight="700">Inner CL TF (P only, G₂=1/(τ₂s+1)):</text>
        <text x={679} y={oy+55} textAnchor="middle" fontSize={10} fill="#333" fontFamily="'Courier New',monospace">G_CL,inner = Kp/(τ₂s+1+Kp) = 1/(τ₂/(1+Kp)·s + 1/(1+Kp)+...)</text>
        <text x={679} y={oy+70} textAnchor="middle" fontSize={9}  fill="#555">Effective: τ_eff = τ₂/(1+Kp),  K_eff = Kp/(1+Kp) ≈ 1 for large Kp</text>

        {/* BW table */}
        <text x={476} y={oy+92} fontSize={10} fill="#7B0D0D" fontWeight="700">Inner closed-loop bandwidth vs gain:</text>
        <rect x={476} y={oy+98} width={406} height={22} rx={2} fill="#27ae60"/>
        {['Kp_inner','Pole location','BW approx.','Relative to outer'].map((h,i) => (
          <text key={i} x={[484,570,660,750][i]} y={oy+113} fontSize={9} fill="#fff" fontWeight="700">{h}</text>
        ))}
        {configs.map(({ KpInner, color }, i) => {
          const bw = innerBWs[i].toFixed(1);
          const ratio = (innerBWs[i]/5).toFixed(0); // outer BW ≈ 5 rad/s (rough)
          return (
            <g key={i}>
              <rect x={476} y={oy+120+i*20} width={406} height={20} fill={i%2===0?'#f0fff4':'#fff'}/>
              <text x={484} y={oy+134+i*20} fontSize={9.5} fill={color} fontWeight="700">{KpInner}</text>
              <text x={570} y={oy+134+i*20} fontSize={9}   fill="#333" fontFamily="'Courier New',monospace">s = −{((1+KpInner)/0.2).toFixed(1)}</text>
              <text x={660} y={oy+134+i*20} fontSize={9}   fill="#333" fontFamily="'Courier New',monospace">≈{bw} r/s</text>
              <text x={750} y={oy+134+i*20} fontSize={9}   fill={+ratio>=3?'#27ae60':'#c0392b'} fontWeight="700">{ratio}× faster</text>
            </g>
          );
        })}

        {/* Design rule */}
        <rect x={476} y={oy+205} width={406} height={46} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.2"/>
        <text x={679} y={oy+220} textAnchor="middle" fontSize={10} fill="#7B0D0D" fontWeight="700">Design Rule:</text>
        <text x={679} y={oy+235} textAnchor="middle" fontSize={9.5} fill="#333">Inner loop BW must be 3–10× outer loop BW</text>
        <text x={679} y={oy+249} textAnchor="middle" fontSize={9}   fill="#555">If inner is too slow: no benefit over single-loop</text>
        <text x={679} y={oy+263} textAnchor="middle" fontSize={9}   fill="#555">If inner is too fast: measurement noise amplified</text>

        {/* Tuning procedure */}
        <rect x={476} y={oy+258} width={406} height={60} rx={3} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.2"/>
        <text x={679} y={oy+272} textAnchor="middle" fontSize={10} fill="#7B0D0D" fontWeight="700">Cascade Tuning Procedure:</text>
        {['1. Open outer loop (set outer controller to manual)',
          '2. Tune inner loop: fast response, minimal OS',
          '3. Close inner loop, verify stability independently',
          '4. Tune outer loop with inner loop closed (treat as fast plant)'].map((line, j) => (
          <text key={j} x={484} y={oy+286+j*14} fontSize={8.5} fill="#333">{line}</text>
        ))}
      </svg>
      <div style={S.dCaption}>As the inner-loop proportional gain increases, the inner closed-loop time constant shrinks (τ_eff = τ₂/(1+Kp)) and the outer loop "sees" a faster, unity-gain inner plant. This allows the outer PI controller to be more aggressive, resulting in faster outer-loop step responses. The improvement saturates once the inner loop is much faster than the outer — confirming the 3–10× bandwidth ratio rule.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 4 — Feedforward Control Block Diagram
// ─────────────────────────────────────────────────────────────────
function DiagramFeedforwardStructure() {
  const W = 900, H = 380;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Feedforward Control — Reference FF, Disturbance FF &amp; Combined Architecture</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <Arr id="ffs-blk" color="#555"/>
          <Arr id="ffs-red" color="#c0392b"/>
          <Arr id="ffs-grn" color="#27ae60"/>
          <Arr id="ffs-blu" color="#2E75B6"/>
          <Arr id="ffs-orn" color="#e07b00"/>
        </defs>

        {/* ── TOP LEFT: Reference Feedforward ── */}
        <rect x={10} y={10} width={420} height={168} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={220} y={26} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Reference (Setpoint) Feedforward</text>

        {/* R(s) splits */}
        <text x={18} y={102} fontSize={11} fill="#c0392b" fontWeight="700">R(s)</text>
        <line x1={42} y1={98} x2={80} y2={98} stroke="#c0392b" strokeWidth="2"/>
        <circle cx={80} cy={98} r={4} fill="#c0392b"/>

        {/* FF path top */}
        <line x1={80} y1={98} x2={80} y2={56} stroke="#2E75B6" strokeWidth="1.8"/>
        <line x1={80} y1={56} x2={130} y2={56} stroke="#2E75B6" strokeWidth="1.8" markerEnd="url(#ffs-blu)"/>
        <rect x={130} y={40} width={80} height={32} rx={3} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={170} y={55} textAnchor="middle" fontSize={11} fill="#2E75B6" fontWeight="700">F_r(s)</text>
        <text x={170} y={67} textAnchor="middle" fontSize={8.5} fill="#555">FF compensator</text>

        {/* FB path */}
        <line x1={80} y1={98} x2={130} y2={98} stroke="#c0392b" strokeWidth="1.8" markerEnd="url(#ffs-red)"/>
        <circle cx={96} cy={98} r={14} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={96} y={103} textAnchor="middle" fontSize={13} fill="#c0392b" fontWeight="700">Σ</text>
        <text x={80} y={94}  fontSize={9} fill="#27ae60" fontWeight="700">+</text>
        <text x={92} y={112} fontSize={9} fill="#c0392b" fontWeight="700">−</text>

        <rect x={114} y={82} width={80} height={32} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={154} y={98} textAnchor="middle" fontSize={11} fill="#c0392b" fontWeight="700">C(s)</text>
        <text x={154} y={109} textAnchor="middle" fontSize={8.5} fill="#555">FB controller</text>
        <line x1={194} y1={98} x2={238} y2={98} stroke="#555" strokeWidth="1.8" markerEnd="url(#ffs-blk)"/>

        {/* Sum FF + FB */}
        <line x1={210} y1={56} x2={250} y2={56} stroke="#2E75B6" strokeWidth="1.8"/>
        <line x1={250} y1={56} x2={250} y2={88} stroke="#2E75B6" strokeWidth="1.8" markerEnd="url(#ffs-blu)"/>
        <circle cx={250} cy={98} r={14} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={250} y={103} textAnchor="middle" fontSize={13} fill="#2E75B6" fontWeight="700">Σ</text>
        <line x1={264} y1={98} x2={300} y2={98} stroke="#555" strokeWidth="1.8" markerEnd="url(#ffs-blk)"/>

        {/* Plant */}
        <rect x={300} y={82} width={80} height={32} rx={3} fill="#f5d0ff" stroke="#9b59b6" strokeWidth="1.5"/>
        <text x={340} y={98} textAnchor="middle" fontSize={11} fill="#9b59b6" fontWeight="700">G(s)</text>
        <line x1={380} y1={98} x2={418} y2={98} stroke="#27ae60" strokeWidth="1.8" markerEnd="url(#ffs-grn)"/>
        <text x={398} y={90} fontSize={9.5} fill="#27ae60" fontWeight="700">Y(s)</text>
        <circle cx={400} cy={98} r={3} fill="#27ae60"/>
        <line x1={400} y1={101} x2={400} y2={142} stroke="#555" strokeWidth="1.5"/>
        <line x1={400} y1={142} x2={96}  y2={142} stroke="#555" strokeWidth="1.5"/>
        <line x1={96}  y1={142} x2={96}  y2={112} stroke="#555" strokeWidth="1.5" markerEnd="url(#ffs-blk)"/>

        {/* Ideal condition label */}
        <rect x={14} y={150} width={408} height={22} rx={2} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1"/>
        <text x={218} y={164} textAnchor="middle" fontSize={9.5} fill="#2E75B6" fontFamily="'Courier New',monospace">
          Ideal: F_r(s) = 1/G(s) → Y(s)/R(s) = 1  (perfect reference tracking, loop-independent)
        </text>

        {/* ── TOP RIGHT: Disturbance Feedforward ── */}
        <rect x={445} y={10} width={445} height={168} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={667} y={26} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Disturbance Feedforward</text>

        {/* D(s) measured */}
        <text x={453} y={75} fontSize={11} fill="#e07b00" fontWeight="700">D(s)</text>
        <line x1={478} y1={71} x2={518} y2={71} stroke="#e07b00" strokeWidth="2"/>
        <circle cx={518} cy={71} r={4} fill="#e07b00"/>
        <line x1={518} y1={71} x2={518} y2={52} stroke="#e07b00" strokeWidth="1.8"/>
        <line x1={518} y1={52} x2={568} y2={52} stroke="#e07b00" strokeWidth="1.8" markerEnd="url(#ffs-orn)"/>

        {/* FF block */}
        <rect x={568} y={36} width={80} height={32} rx={3} fill="#fff5e6" stroke="#e07b00" strokeWidth="1.5"/>
        <text x={608} y={51} textAnchor="middle" fontSize={11} fill="#e07b00" fontWeight="700">F_d(s)</text>
        <text x={608} y={63} textAnchor="middle" fontSize={8.5} fill="#555">disturbance FF</text>

        {/* Disturbance also enters plant summing */}
        <line x1={518} y1={71} x2={672} y2={71} stroke="#e07b00" strokeWidth="1.8"/>
        <circle cx={672} cy={71} r={13} fill="#fff5e6" stroke="#e07b00" strokeWidth="1.5"/>
        <text x={672} y={76} textAnchor="middle" fontSize={13} fill="#e07b00" fontWeight="700">Σ</text>
        <line x1={685} y1={71} x2={720} y2={71} stroke="#555" strokeWidth="1.8" markerEnd="url(#ffs-blk)"/>

        {/* R input → FB → summing */}
        <text x={453} y={116} fontSize={11} fill="#c0392b" fontWeight="700">R(s)</text>
        <line x1={478} y1={112} x2={505} y2={112} stroke="#c0392b" strokeWidth="2" markerEnd="url(#ffs-red)"/>
        <circle cx={523} cy={112} r={13} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={523} y={117} textAnchor="middle" fontSize={13} fill="#c0392b" fontWeight="700">Σ</text>
        <line x1={536} y1={112} x2={568} y2={112} stroke="#555" strokeWidth="1.8" markerEnd="url(#ffs-blk)"/>
        <rect x={568} y={96} width={80} height={32} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={608} y={115} textAnchor="middle" fontSize={11} fill="#c0392b" fontWeight="700">C(s)</text>
        <line x1={648} y1={112} x2={672} y2={112} stroke="#555" strokeWidth="1.8"/>
        <line x1={672} y1={84}  x2={672} y2={98}  stroke="#555" strokeWidth="1.8"/>

        {/* FF sum into plant input */}
        <line x1={648} y1={52} x2={672} y2={52} stroke="#e07b00" strokeWidth="1.8"/>
        <line x1={672} y1={52} x2={672} y2={58} stroke="#e07b00" strokeWidth="1.8" markerEnd="url(#ffs-orn)"/>

        {/* Plant G */}
        <rect x={720} y={55} width={70} height={32} rx={3} fill="#f5d0ff" stroke="#9b59b6" strokeWidth="1.5"/>
        <text x={755} y={72} textAnchor="middle" fontSize={11} fill="#9b59b6" fontWeight="700">G(s)</text>
        <line x1={790} y1={71} x2={882} y2={71} stroke="#27ae60" strokeWidth="1.8" markerEnd="url(#ffs-grn)"/>
        <circle cx={840} cy={71} r={3} fill="#27ae60"/>
        <text x={800} y={63} fontSize={9.5} fill="#27ae60" fontWeight="700">Y(s)</text>
        <line x1={840} y1={74} x2={840} y2={148} stroke="#555" strokeWidth="1.5"/>
        <line x1={840} y1={148} x2={523} y2={148} stroke="#555" strokeWidth="1.5"/>
        <line x1={523} y1={148} x2={523} y2={125} stroke="#555" strokeWidth="1.5" markerEnd="url(#ffs-blk)"/>

        {/* Ideal condition */}
        <rect x={449} y={150} width={440} height={22} rx={2} fill="#fff5e6" stroke="#e07b00" strokeWidth="1"/>
        <text x={669} y={164} textAnchor="middle" fontSize={9.5} fill="#e07b00" fontFamily="'Courier New',monospace">
          Ideal: F_d(s) = −G_d(s)/G(s) → D has no effect on Y  (perfect disturbance cancellation)
        </text>

        {/* ── BOTTOM: Key concepts ── */}
        <rect x={10} y={188} width={880} height={184} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={450} y={206} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Feedforward Principles — Properties &amp; Limitations</text>

        {[
          { x:18,  color:'#2E75B6', bg:'#eaf3ff',
            title:'Reference FF Principle',
            lines:['Acts before the error is detected',
                   'F_r(s) compensates for G(s) dynamics',
                   'Y(s)/R(s) = G(s)F_r(s) with FF only',
                   'Ideal: F_r(s) = G⁻¹(s) → Y=R instantly',
                   'Combined: FB corrects FF errors'] },
          { x:234, color:'#e07b00', bg:'#fff5e6',
            title:'Disturbance FF Principle',
            lines:['Measure disturbance directly at source',
                   'Compute cancellation signal F_d(s)·D(s)',
                   'Inject into control input to cancel D effect',
                   'Ideal: F_d(s) = −G_d(s)/G(s)',
                   'FB loop handles residual errors'] },
          { x:450, color:'#27ae60', bg:'#f0fff4',
            title:'Advantages of FF',
            lines:['Responds before error occurs (proactive)',
                   'Does not affect closed-loop stability',
                   'Can achieve faster disturbance rejection',
                   'Reduces burden on FB controller',
                   'Improves tracking without gain increase'] },
          { x:666, color:'#c0392b', bg:'#FEF0F0',
            title:'Limitations of FF',
            lines:['Requires accurate plant model G(s)',
                   'Sensitive to modelling errors',
                   'Cannot correct unknown disturbances',
                   'F_r=1/G may be non-causal (improper)',
                   'Always needs FB for robustness'] },
        ].map(({ x, color, bg, title, lines }) => (
          <g key={x}>
            <rect x={x+2} y={214} width={210} height={150} rx={3} fill={bg} stroke={color} strokeWidth="1.2"/>
            <text x={x+10} y={228} fontSize={10} fill={color} fontWeight="700">{title}</text>
            {lines.map((l, j) => (
              <text key={j} x={x+10} y={243+j*17} fontSize={8.5} fill="#333">▸ {l}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Reference feedforward (left) uses F_r(s)=1/G(s) to invert the plant dynamics so the output tracks the reference without waiting for an error to build up. Disturbance feedforward (right) measures the disturbance D(s) directly and injects a cancellation signal F_d(s)·D(s) into the control input. Both strategies act proactively — before errors appear — but require an accurate plant model and always need a feedback loop for robustness.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 5 — Ideal vs Practical Feedforward (computed responses)
// ─────────────────────────────────────────────────────────────────
function DiagramFFComparison() {
  const W = 900, H = 360;
  const dt=0.02, tMax=18;

  // Disturbance: step at t=4
  const dist = (t) => t >= 4 ? 1.0 : 0;

  // 1. Feedback only (PI)
  const fbOnly = simFeedforward(3, 0.8, 0, tMax, dt, dist, 0);

  // 2. Perfect FF (F_d = -1, ideal gain matches disturbance path)
  const perfectFF = simFeedforward(3, 0.8, 0, tMax, dt, dist, -1.0);

  // 3. Imperfect FF (gain mismatch: F_d = -0.6)
  const imperfectFF = simFeedforward(3, 0.8, 0, tMax, dt, dist, -0.6);

  // 4. FF + FB (combined): slight mismatch corrected by feedback
  const ffPlusFB = simFeedforward(3, 0.8, 0, tMax, dt, dist, -0.85);

  // Reference FF comparison: step, no disturbance
  const noFF  = simFeedforward(2, 0.5, 0,   tMax, dt, null, 0);  // FB only
  const withFF= simFeedforward(2, 0.5, 0.8, tMax, dt, null, 0);  // with ref FF

  const PW=420, PH=240;
  const ox=20, oy=24;
  const sx = t  => ox+34+(t/tMax)*(PW-50);
  const sy = yv => oy+PH-22-Math.max(Math.min(yv,1.6),-0.3)*(PH-36)/1.9;

  // Right panel: reference tracking
  const ox2=464, PW2=426;
  const sx2 = t  => ox2+34+(t/tMax)*(PW2-50);
  const sy2 = yv => oy+PH-22-Math.max(Math.min(yv,1.5),-0.1)*(PH-36)/1.6;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Feedforward Performance — Ideal, Imperfect, and Combined FF+FB (Computed)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <Arr id="ffc-blk" color="#aaa"/>
          <clipPath id="ffc-l"><rect x={54} y={26} width={PW-48} height={PH-20}/></clipPath>
          <clipPath id="ffc-r"><rect x={ox2+32} y={26} width={PW2-48} height={PH-20}/></clipPath>
        </defs>

        {/* LEFT: Disturbance rejection */}
        <rect x={ox} y={oy} width={PW} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={ox+PW/2} y={oy-8} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Disturbance Rejection — Step D at t=4</text>

        {[4,8,12,16,18].map(tv=>(
          <line key={tv} x1={sx(tv)} y1={oy+6} x2={sx(tv)} y2={oy+PH-20} stroke="#f8f8f8" strokeWidth="1"/>
        ))}
        {[0.5,1.0].map(yv=>(
          <line key={yv} x1={ox+32} y1={sy(yv)} x2={ox+PW-14} y2={sy(yv)} stroke="#f5f5f5" strokeWidth="1"/>
        ))}

        {/* Disturbance band */}
        <rect x={sx(4)} y={oy+6} width={PW-50+ox+34-sx(4)} height={PH-24} fill="#e07b00" opacity="0.04"/>
        <line x1={sx(4)} y1={oy+6} x2={sx(4)} y2={oy+PH-20} stroke="#e07b00" strokeWidth="1.2" strokeDasharray="4,3" opacity="0.5"/>
        <text x={sx(4)+4} y={oy+18} fontSize={8.5} fill="#e07b00" fontWeight="700">D step at t=4</text>

        <line x1={ox+32} y1={sy(1)} x2={ox+PW-14} y2={sy(1)} stroke="#27ae60" strokeWidth="1.2" strokeDasharray="5,4" opacity="0.6"/>
        <line x1={ox+32} y1={oy+PH-20} x2={ox+PW-8} y2={oy+PH-20} stroke="#aaa" strokeWidth="1.2" markerEnd="url(#ffc-blk)"/>
        <line x1={ox+32} y1={oy+PH-20} x2={ox+32}   y2={oy+4}     stroke="#aaa" strokeWidth="1.2" markerEnd="url(#ffc-blk)"/>
        <text x={ox+PW-4} y={oy+PH-16} fontSize={8} fill="#aaa">t</text>

        <path d={mkPath(fbOnly,      sx, sy, 'y')} fill="none" stroke="#c0392b" strokeWidth="2.2" clipPath="url(#ffc-l)"/>
        <path d={mkPath(perfectFF,   sx, sy, 'y')} fill="none" stroke="#27ae60" strokeWidth="2.2" clipPath="url(#ffc-l)"/>
        <path d={mkPath(imperfectFF, sx, sy, 'y')} fill="none" stroke="#e07b00" strokeWidth="1.8" strokeDasharray="6,3" clipPath="url(#ffc-l)"/>
        <path d={mkPath(ffPlusFB,    sx, sy, 'y')} fill="none" stroke="#2E75B6" strokeWidth="2.2" clipPath="url(#ffc-l)"/>

        {/* Legend */}
        {[
          {c:'#c0392b', d:'none',lbl:'FB only (PI)'},
          {c:'#27ae60', d:'none',lbl:'Perfect FF (Fd=−1)'},
          {c:'#e07b00', d:'6,3', lbl:'Imperfect FF (Fd=−0.6)'},
          {c:'#2E75B6', d:'none',lbl:'FF+FB (Fd=−0.85)'},
        ].map(({c,d,lbl},i) => (
          <g key={i}>
            <line x1={ox+36+i*96} y1={oy+PH-10} x2={ox+54+i*96} y2={oy+PH-10} stroke={c} strokeWidth="2" strokeDasharray={d==='none'?undefined:d}/>
            <text x={ox+58+i*96} y={oy+PH-6} fontSize={8} fill={c}>{lbl}</text>
          </g>
        ))}

        {/* RIGHT: Reference tracking */}
        <rect x={ox2} y={oy} width={PW2} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={ox2+PW2/2} y={oy-8} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Reference Tracking — With vs Without FF</text>

        {[4,8,12,16].map(tv=>(
          <line key={tv} x1={sx2(tv)} y1={oy+6} x2={sx2(tv)} y2={oy+PH-20} stroke="#f8f8f8" strokeWidth="1"/>
        ))}
        <line x1={ox2+32} y1={sy2(1)} x2={ox2+PW2-14} y2={sy2(1)} stroke="#27ae60" strokeWidth="1.2" strokeDasharray="5,4" opacity="0.6"/>
        <line x1={ox2+32} y1={oy+PH-20} x2={ox2+PW2-8} y2={oy+PH-20} stroke="#aaa" strokeWidth="1.2" markerEnd="url(#ffc-blk)"/>
        <line x1={ox2+32} y1={oy+PH-20} x2={ox2+32}    y2={oy+4}     stroke="#aaa" strokeWidth="1.2" markerEnd="url(#ffc-blk)"/>

        <path d={mkPath(noFF,   sx2, sy2, 'y')} fill="none" stroke="#c0392b" strokeWidth="2.2" clipPath="url(#ffc-r)"/>
        <path d={mkPath(withFF, sx2, sy2, 'y')} fill="none" stroke="#2E75B6" strokeWidth="2.2" clipPath="url(#ffc-r)"/>

        {/* Annotations */}
        <text x={sx2(2.5)} y={sy2(0.55)} fontSize={9} fill="#c0392b" fontWeight="700">FB only: slow rise</text>
        <text x={sx2(1.2)} y={sy2(0.92)} fontSize={9} fill="#2E75B6" fontWeight="700">FF+FB: fast rise</text>

        {[
          {c:'#c0392b', lbl:'FB only (Kp=2, Ki=0.5)'},
          {c:'#2E75B6', lbl:'FF + FB (FF gain=0.8)'},
        ].map(({c,lbl},i) => (
          <g key={i}>
            <line x1={ox2+36+i*180} y1={oy+PH-10} x2={ox2+54+i*180} y2={oy+PH-10} stroke={c} strokeWidth="2"/>
            <text x={ox2+58+i*180} y={oy+PH-6} fontSize={8.5} fill={c}>{lbl}</text>
          </g>
        ))}

        {/* Bottom notes */}
        <rect x={10} y={oy+PH+12} width={880} height={72} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.2"/>
        <text x={450} y={oy+PH+28} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Key Observations from Simulation</text>
        {[
          ['Perfect FF (left, green):',  'Zero steady-state error to disturbance — instantaneous compensation. Requires exact model.'],
          ['Imperfect FF (left, orange):', 'Partial cancellation only — residual error remains. FB loop corrects it slowly.'],
          ['FF + FB (left, blue):',        'Slight FF mismatch fully corrected by feedback — best practical choice.'],
          ['Ref FF (right, blue):',        'Faster initial rise without increasing FB gain — reference tracking improved without destabilising.'],
        ].map(([bold, rest], i) => (
          <g key={i}>
            <text x={24} y={oy+PH+44+i*14} fontSize={9} fill="#7B0D0D" fontWeight="700">{bold}</text>
            <text x={180} y={oy+PH+44+i*14} fontSize={9} fill="#555">{rest}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Left panel: perfect feedforward (green) achieves near-zero disturbance effect while FB-only (red) shows a large transient. Imperfect FF (orange, gain mismatch) partially cancels the disturbance — better than FB alone but with residual error. The combined FF+FB (blue) uses a slightly mismatched FF corrected by the feedback loop — the practically optimal strategy. Right panel: reference feedforward significantly speeds up tracking without requiring increased feedback gain.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 6 — Feedforward Compensator Design (frequency domain)
// ─────────────────────────────────────────────────────────────────
function DiagramFFFrequency() {
  const W = 900, H = 380;

  // Plant: G(s) = 1/((s+1)(0.5s+1)) — Bode
  // Ideal FF for reference: F_r(s) = 1/G(s) = (s+1)(0.5s+1)
  // Practical (filtered): F_r_prac(s) = (s+1)(0.5s+1) / (0.05s+1)^2
  // Disturbance path: G_d(s) = 1/(0.3s+1)
  // Ideal disturbance FF: F_d(s) = -G_d(s)/G(s) = -(s+1)(0.5s+1)/(0.3s+1)

  const Npts = 200, wMin = 0.05, wMax = 200;
  const wArr = [];
  for (let i=0; i<=Npts; i++) {
    wArr.push(wMin * Math.pow(wMax/wMin, i/Npts));
  }

  // Plant Bode
  const gMag = w => 20*Math.log10(1 / (Math.sqrt(1+w*w) * Math.sqrt(1+(0.5*w)**2)));
  const gPhs = w => -(Math.atan(w) + Math.atan(0.5*w)) * 180/Math.PI;

  // Ideal FF = 1/G: magnitude = -gMag, phase = -gPhs
  const frIdMag = w => -gMag(w);
  const frIdPhs = w => -gPhs(w);

  // Practical FF: add low-pass filter pole pair at 20 rad/s
  const frPrMag = w => frIdMag(w) + 20*Math.log10(1/(Math.sqrt(1+(w/20)**2)**2));
  const frPrPhs = w => frIdPhs(w) - 2*Math.atan(w/20)*180/Math.PI;

  // Closed-loop sensitivity S = 1/(1+GC), complement T = GC/(1+GC)
  // With C(s) = 3 + 0.8/s (PI)
  const CPI = w => {
    const re = 3, im = -0.8/w;
    return { re, im, mag: Math.sqrt(re*re+im*im) };
  };
  const sData = wArr.map(w => {
    const gMagLin = 1/(Math.sqrt(1+w*w)*Math.sqrt(1+(0.5*w)**2));
    const gPhsRad = -(Math.atan(w)+Math.atan(0.5*w));
    const gRe = gMagLin*Math.cos(gPhsRad);
    const gIm = gMagLin*Math.sin(gPhsRad);
    const c = CPI(w);
    // GC
    const gcRe = gRe*c.re - gIm*c.im;
    const gcIm = gRe*c.im + gIm*c.re;
    // S = 1/(1+GC)
    const dRe = 1+gcRe, dIm = gcIm;
    const dMag2 = dRe*dRe + dIm*dIm;
    const sMag = 1/Math.sqrt(dMag2);
    return 20*Math.log10(sMag);
  });

  // Plot area
  const PW=400, PH=140;
  const mOx=34, mOy=28, pOx=34, pOy=196;
  const swx = w   => mOx+36+(Math.log10(w/wMin)/Math.log10(wMax/wMin))*(PW-52);
  const smag = dB => mOy+PH-18-((Math.min(50,Math.max(-60,dB))+60)/110)*(PH-28);
  const sphs = ph => pOy+PH-18-((ph+200)/380)*(PH-28);
  const ssensMag = dB => mOy+PH-18-((Math.min(10,Math.max(-50,dB))+50)/60)*(PH-28);

  const mkBode = (fn) => wArr.map((w,i) =>
    `${i===0?'M':'L'}${swx(w).toFixed(1)},${smag(fn(w)).toFixed(1)}`).join(' ');
  const mkPhs  = (fn) => wArr.map((w,i) =>
    `${i===0?'M':'L'}${swx(w).toFixed(1)},${sphs(fn(w)).toFixed(1)}`).join(' ');
  const sensMagPath = wArr.map((w,i) =>
    `${i===0?'M':'L'}${swx(w).toFixed(1)},${ssensMag(sData[i]).toFixed(1)}`).join(' ');

  const wTicks = [0.1,0.2,0.5,1,2,5,10,20,50,100,200];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Feedforward Compensator Design — Frequency-Domain Analysis</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <clipPath id="ffq-m"><rect x={mOx+34} y={mOy+4} width={PW-52} height={PH-22}/></clipPath>
          <clipPath id="ffq-p"><rect x={pOx+34} y={pOy+4} width={PW-52} height={PH-22}/></clipPath>
          <clipPath id="ffq-s"><rect x={470+34} y={mOy+4} width={PW-52} height={PH-22}/></clipPath>
          <clipPath id="ffq-sp"><rect x={470+34} y={pOy+4} width={PW-52} height={PH-22}/></clipPath>
        </defs>

        {/* ── LEFT: FF Bode ── */}
        {/* Magnitude */}
        <rect x={mOx} y={mOy} width={PW} height={PH} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={mOx+PW/2} y={mOy-8} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">FF Compensator Magnitude [dB]</text>

        {wTicks.map(wv => (
          <g key={wv}>
            <line x1={swx(wv)} y1={mOy+6} x2={swx(wv)} y2={mOy+PH-16} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={swx(wv)} y={mOy+PH-4} textAnchor="middle" fontSize={7.5} fill="#ccc">{wv}</text>
          </g>
        ))}
        {[-60,-40,-20,0,20,40,50].map(mv => (
          <g key={mv}>
            <line x1={mOx+34} y1={smag(mv)} x2={mOx+PW-16} y2={smag(mv)} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={mOx+30} y={smag(mv)+3} textAnchor="end" fontSize={7} fill="#ccc">{mv}</text>
          </g>
        ))}
        <line x1={mOx+34} y1={mOy+PH-16} x2={mOx+PW-12} y2={mOy+PH-16} stroke="#aaa" strokeWidth="1.2"/>
        <line x1={mOx+34} y1={mOy+8}     x2={mOx+34}    y2={mOy+PH-16} stroke="#aaa" strokeWidth="1.2"/>

        {/* Plant */}
        <path d={mkBode(gMag)}   fill="none" stroke="#9b59b6" strokeWidth="1.8" clipPath="url(#ffq-m)"/>
        {/* Ideal FF */}
        <path d={mkBode(frIdMag)} fill="none" stroke="#c0392b" strokeWidth="2" strokeDasharray="8,4" clipPath="url(#ffq-m)"/>
        {/* Practical FF */}
        <path d={mkBode(frPrMag)} fill="none" stroke="#27ae60" strokeWidth="2" clipPath="url(#ffq-m)"/>

        {/* Phase */}
        <rect x={pOx} y={pOy} width={PW} height={PH} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={pOx+PW/2} y={pOy-8} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">FF Compensator Phase [°]</text>

        {wTicks.map(wv => (
          <line key={wv} x1={swx(wv)} y1={pOy+6} x2={swx(wv)} y2={pOy+PH-16} stroke="#f5f5f5" strokeWidth="1"/>
        ))}
        {[-180,-90,-45,0,45,90,180].map(pv => (
          <g key={pv}>
            <line x1={pOx+34} y1={sphs(pv)} x2={pOx+PW-16} y2={sphs(pv)} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={pOx+30} y={sphs(pv)+3} textAnchor="end" fontSize={7} fill="#ccc">{pv}°</text>
          </g>
        ))}
        <line x1={pOx+34} y1={pOy+PH-16} x2={pOx+PW-12} y2={pOy+PH-16} stroke="#aaa" strokeWidth="1.2"/>
        <line x1={pOx+34} y1={pOy+8}     x2={pOx+34}    y2={pOy+PH-16} stroke="#aaa" strokeWidth="1.2"/>

        <path d={mkPhs(gPhs)}    fill="none" stroke="#9b59b6" strokeWidth="1.8" clipPath="url(#ffq-p)"/>
        <path d={mkPhs(frIdPhs)} fill="none" stroke="#c0392b" strokeWidth="2" strokeDasharray="8,4" clipPath="url(#ffq-p)"/>
        <path d={mkPhs(frPrPhs)} fill="none" stroke="#27ae60" strokeWidth="2" clipPath="url(#ffq-p)"/>
        <text x={pOx+36} y={pOy+PH-4} fontSize={7.5} fill="#aaa">ω [rad/s]</text>

        {/* Left legend */}
        {[
          {c:'#9b59b6', d:'none', lbl:'Plant G(s)'},
          {c:'#c0392b', d:'8,4',  lbl:'Ideal FF = 1/G(s)'},
          {c:'#27ae60', d:'none', lbl:'Practical FF (filtered)'},
        ].map(({c,d,lbl},i) => (
          <g key={i}>
            <line x1={mOx+4} y1={pOy+PH+4+i*12} x2={mOx+22} y2={pOy+PH+4+i*12}
              stroke={c} strokeWidth="1.8" strokeDasharray={d==='none'?undefined:d}/>
            <text x={mOx+26} y={pOy+PH+8+i*12} fontSize={8.5} fill={c}>{lbl}</text>
          </g>
        ))}

        {/* ── RIGHT: Sensitivity + complementary ── */}
        <rect x={470} y={mOy} width={PW} height={PH} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={670} y={mOy-8} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Closed-Loop Sensitivity S(jω) with PI</text>

        {wTicks.map(wv => (
          <g key={wv}>
            <line x1={470+36+(Math.log10(wv/wMin)/Math.log10(wMax/wMin))*(PW-52)} y1={mOy+6}
                  x2={470+36+(Math.log10(wv/wMin)/Math.log10(wMax/wMin))*(PW-52)} y2={mOy+PH-16} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={470+36+(Math.log10(wv/wMin)/Math.log10(wMax/wMin))*(PW-52)} y={mOy+PH-4}
              textAnchor="middle" fontSize={7.5} fill="#ccc">{wv}</text>
          </g>
        ))}
        {[-50,-30,-20,-10,0,5,10].map(mv => {
          const sy_s = ssensMag;
          return (
            <g key={mv}>
              <line x1={470+34} y1={sy_s(mv)} x2={470+PW-16} y2={sy_s(mv)} stroke="#f5f5f5" strokeWidth="1"/>
              <text x={470+30} y={sy_s(mv)+3} textAnchor="end" fontSize={7} fill="#ccc">{mv}</text>
            </g>
          );
        })}
        <line x1={470+34} y1={ssensMag(0)} x2={470+PW-12} y2={ssensMag(0)} stroke="#aaa" strokeWidth="1" strokeDasharray="4,3"/>
        <text x={470+PW-10} y={ssensMag(0)-3} fontSize={7.5} fill="#aaa">0dB</text>
        <line x1={470+34} y1={mOy+PH-16} x2={470+PW-12} y2={mOy+PH-16} stroke="#aaa" strokeWidth="1.2"/>
        <line x1={470+34} y1={mOy+8}     x2={470+34}    y2={mOy+PH-16} stroke="#aaa" strokeWidth="1.2"/>

        <path d={sensMagPath} fill="none" stroke="#c0392b" strokeWidth="2" clipPath="url(#ffq-s)"/>

        {/* Annotations on sensitivity */}
        <text x={670} y={mOy+26} textAnchor="middle" fontSize={9} fill="#2E75B6" fontWeight="700">
          S(jω) = 1/(1+G(s)C(s))
        </text>
        <text x={670} y={mOy+40} textAnchor="middle" fontSize={9} fill="#555">
          Sensitivity = error due to disturbance D in feedback loop
        </text>
        <text x={670} y={mOy+54} textAnchor="middle" fontSize={9} fill="#555">
          FF improves: actual error = S(s)·D + G(s)·F_d(s)·D
        </text>

        {/* Right legend + formula panel */}
        <rect x={470} y={pOy} width={PW} height={PH} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={670} y={pOy+18} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Design Formulas for FF Compensators</text>

        {[
          { y:pOy+28, fill:'#eaf3ff', bc:'#2E75B6', title:'Reference FF (ideal)',
            body:'F_r(s) = 1/G(s)  →  improper if G has more poles than zeros\nPractical: F_r(s) = (numerator of G) / (denominator × extra poles)\nChoose extra poles fast enough to not limit tracking bandwidth' },
          { y:pOy+78, fill:'#fff5e6', bc:'#e07b00', title:'Disturbance FF (ideal)',
            body:'F_d(s) = −G_d(s)/G(s)  where G_d = disturbance-to-output path\nIf G_d(s)=G(s): F_d(s) = −1  (simple gain feedforward)\nIf paths differ: static gain F_d = −G_d(0)/G(0) as minimum' },
          { y:pOy+130, fill:'#f0fff4', bc:'#27ae60', title:'Partial (static) FF',
            body:'F_d = −K_d/K  (ratio of DC gains) → removes steady-state error only\nSimplest implementation; no dynamics required\nUseful when full dynamic model of disturbance path is unavailable' },
        ].map(({ y, fill, bc, title, body }) => (
          <g key={y}>
            <rect x={478} y={y} width={PW-16} height={body.split('\n').length*18+18} rx={3} fill={fill} stroke={bc} strokeWidth="1"/>
            <text x={486} y={y+14} fontSize={10} fill="#7B0D0D" fontWeight="700">{title}</text>
            {body.split('\n').map((l,j) => (
              <text key={j} x={486} y={y+28+j*16} fontSize={8.5} fill="#333" fontFamily="'Courier New',monospace">{l}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The ideal reference feedforward F_r(s)=1/G(s) is the exact inverse of the plant — its magnitude is the mirror image of G(s) and its phase is opposite. This is usually improper (more zeros than poles) and must be filtered to be implementable. The sensitivity function S(jω) shows where the feedback loop provides disturbance rejection — feedforward complements this by cancelling disturbances the feedback loop cannot react to quickly enough.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 7 — Combined Cascade + Feedforward Architecture
// ─────────────────────────────────────────────────────────────────
function DiagramCombinedArchitecture() {
  const W = 900, H = 380;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Combined Cascade + Feedforward — Full Advanced Control Architecture</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <Arr id="ca-blk" color="#555"/>
          <Arr id="ca-red" color="#c0392b"/>
          <Arr id="ca-grn" color="#27ae60"/>
          <Arr id="ca-blu" color="#2E75B6"/>
          <Arr id="ca-orn" color="#e07b00"/>
          <Arr id="ca-pur" color="#9b59b6"/>
        </defs>

        {/* Background zones */}
        <rect x={12} y={30} width={876} height={210} rx={4} fill="#eaf3ff" opacity="0.15"/>
        <rect x={160} y={38} width={600} height={140} rx={4} fill="#f0fff4" opacity="0.3"/>

        <text x={20}  y={44} fontSize={9.5} fill="#2E75B6" fontWeight="700" fontStyle="italic">Outer (primary) loop</text>
        <text x={168} y={52} fontSize={9}   fill="#27ae60" fontWeight="700" fontStyle="italic">Inner (secondary) loop</text>

        {/* Reference FF path */}
        <text x={15} y={82} fontSize={10} fill="#c0392b" fontWeight="700">R(s)</text>
        <line x1={40} y1={78} x2={76} y2={78} stroke="#c0392b" strokeWidth="1.8"/>
        <circle cx={76} cy={78} r={4} fill="#c0392b"/>
        {/* Split to FF and FB */}
        <line x1={76} y1={78} x2={76} y2={52} stroke="#2E75B6" strokeWidth="1.5"/>
        <line x1={76} y1={52} x2={120} y2={52} stroke="#2E75B6" strokeWidth="1.5" markerEnd="url(#ca-blu)"/>
        <rect x={120} y={38} width={70} height={28} rx={3} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={155} y={50} textAnchor="middle" fontSize={10} fill="#2E75B6" fontWeight="700">F_r(s)</text>
        <text x={155} y={62} textAnchor="middle" fontSize={8}  fill="#555">Ref FF</text>

        {/* FB path: outer summing */}
        <line x1={76} y1={78} x2={100} y2={78} stroke="#c0392b" strokeWidth="1.8" markerEnd="url(#ca-red)"/>
        <circle cx={118} cy={78} r={14} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={118} y={83} textAnchor="middle" fontSize={13} fill="#c0392b" fontWeight="700">Σ</text>
        <line x1={132} y1={78} x2={162} y2={78} stroke="#555" strokeWidth="1.8" markerEnd="url(#ca-blk)"/>

        {/* Outer controller */}
        <rect x={162} y={62} width={72} height={32} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={198} y={76} textAnchor="middle" fontSize={11} fill="#c0392b" fontWeight="700">C₁(s)</text>
        <text x={198} y={87} textAnchor="middle" fontSize={8}  fill="#555">Outer PI</text>
        <line x1={234} y1={78} x2={256} y2={78} stroke="#555" strokeWidth="1.8" markerEnd="url(#ca-blk)"/>

        {/* Sum ref FF with outer ctrl output */}
        <line x1={190} y1={52} x2={256} y2={52} stroke="#2E75B6" strokeWidth="1.5"/>
        <line x1={256} y1={52} x2={256} y2={66} stroke="#2E75B6" strokeWidth="1.5" markerEnd="url(#ca-blu)"/>
        <circle cx={256} cy={78} r={14} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={256} y={83} textAnchor="middle" fontSize={13} fill="#2E75B6" fontWeight="700">Σ</text>
        <line x1={270} y1={78} x2={296} y2={78} stroke="#555" strokeWidth="1.8" markerEnd="url(#ca-blk)"/>
        <text x={276} y={70} fontSize={9} fill="#555">r₂</text>

        {/* Inner summing junction */}
        <circle cx={314} cy={78} r={14} fill="#f0fff4" stroke="#27ae60" strokeWidth="1.5"/>
        <text x={314} y={83} textAnchor="middle" fontSize={13} fill="#27ae60" fontWeight="700">Σ</text>
        <line x1={328} y1={78} x2={358} y2={78} stroke="#555" strokeWidth="1.8" markerEnd="url(#ca-blk)"/>

        {/* Inner controller */}
        <rect x={358} y={62} width={70} height={32} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1.5"/>
        <text x={393} y={76} textAnchor="middle" fontSize={11} fill="#27ae60" fontWeight="700">C₂(s)</text>
        <text x={393} y={87} textAnchor="middle" fontSize={8}  fill="#555">Inner P</text>
        <line x1={428} y1={78} x2={458} y2={78} stroke="#555" strokeWidth="1.8" markerEnd="url(#ca-blk)"/>

        {/* Inner disturbance + FF */}
        <text x={432} y={24} fontSize={10} fill="#e07b00" fontWeight="700">D₂(s)</text>
        <line x1={472} y1={24} x2={472} y2={50} stroke="#e07b00" strokeWidth="1.8" strokeDasharray="4,3"/>
        <circle cx={472} cy={30} r={4} fill="#e07b00"/>
        {/* FF from D2 */}
        <line x1={472} y1={30} x2={420} y2={30} stroke="#e07b00" strokeWidth="1.5"/>
        <line x1={420} y1={30} x2={420} y2={52} stroke="#e07b00" strokeWidth="1.5" markerEnd="url(#ca-orn)"/>
        <rect x={368} y={38} width={54} height={22} rx={3} fill="#fff5e6" stroke="#e07b00" strokeWidth="1.2"/>
        <text x={395} y={48} textAnchor="middle" fontSize={9}  fill="#e07b00" fontWeight="700">F_d2</text>
        <text x={395} y={57} textAnchor="middle" fontSize={7.5} fill="#555">D₂ FF</text>
        <line x1={368} y1={49} x2={330} y2={49} stroke="#e07b00" strokeWidth="1.2"/>
        <line x1={330} y1={49} x2={330} y2={64} stroke="#e07b00" strokeWidth="1.2" markerEnd="url(#ca-orn)"/>

        {/* D2 disturbance summing */}
        <circle cx={472} cy={78} r={13} fill="#fff5e6" stroke="#e07b00" strokeWidth="1.2"/>
        <text x={472} y={83} textAnchor="middle" fontSize={12} fill="#e07b00" fontWeight="700">Σ</text>
        <line x1={458} y1={78} x2={456} y2={78} stroke="#555" strokeWidth="1.8"/>
        <line x1={472} y1={50} x2={472} y2={62} stroke="#e07b00" strokeWidth="1.5" markerEnd="url(#ca-orn)"/>
        <line x1={485} y1={78} x2={518} y2={78} stroke="#555" strokeWidth="1.8" markerEnd="url(#ca-blk)"/>

        {/* Inner plant G2 */}
        <rect x={518} y={62} width={72} height={32} rx={3} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={554} y={76} textAnchor="middle" fontSize={11} fill="#2E75B6" fontWeight="700">G₂(s)</text>
        <text x={554} y={87} textAnchor="middle" fontSize={8}  fill="#555">inner plant</text>

        {/* y₂ pick-off → inner FB */}
        <line x1={590} y1={78} x2={640} y2={78} stroke="#555" strokeWidth="1.8"/>
        <circle cx={622} cy={78} r={4} fill="#27ae60"/>
        <text x={596} y={70} fontSize={9} fill="#27ae60">y₂</text>
        <line x1={622} y1={82} x2={622} y2={128} stroke="#27ae60" strokeWidth="1.5"/>
        <line x1={622} y1={128} x2={314} y2={128} stroke="#27ae60" strokeWidth="1.5"/>
        <line x1={314} y1={128} x2={314} y2={92} stroke="#27ae60" strokeWidth="1.5" markerEnd="url(#ca-grn)"/>

        {/* Outer disturbance D1 + FF */}
        <text x={644} y={24} fontSize={10} fill="#9b59b6" fontWeight="700">D₁(s)</text>
        <line x1={668} y1={24} x2={668} y2={50} stroke="#9b59b6" strokeWidth="1.8" strokeDasharray="4,3"/>
        <circle cx={668} cy={30} r={4} fill="#9b59b6"/>
        <line x1={668} y1={30} x2={608} y2={30} stroke="#9b59b6" strokeWidth="1.5"/>
        <line x1={608} y1={30} x2={608} y2={52} stroke="#9b59b6" strokeWidth="1.5" markerEnd="url(#ca-pur)"/>
        <rect x={552} y={38} width={54} height={22} rx={3} fill="#f5d0ff" stroke="#9b59b6" strokeWidth="1.2"/>
        <text x={579} y={48} textAnchor="middle" fontSize={9}  fill="#9b59b6" fontWeight="700">F_d1</text>
        <text x={579} y={57} textAnchor="middle" fontSize={7.5} fill="#555">D₁ FF</text>
        <line x1={552} y1={49} x2={300} y2={49} stroke="#9b59b6" strokeWidth="1.2"/>
        <line x1={300} y1={49} x2={300} y2={64} stroke="#9b59b6" strokeWidth="1.2" markerEnd="url(#ca-pur)"/>

        {/* D1 summing */}
        <circle cx={668} cy={78} r={13} fill="#f5d0ff" stroke="#9b59b6" strokeWidth="1.2"/>
        <text x={668} y={83} textAnchor="middle" fontSize={12} fill="#9b59b6" fontWeight="700">Σ</text>
        <line x1={640} y1={78} x2={652} y2={78} stroke="#555" strokeWidth="1.8" markerEnd="url(#ca-blk)"/>
        <line x1={668} y1={50} x2={668} y2={62} stroke="#9b59b6" strokeWidth="1.5" markerEnd="url(#ca-pur)"/>
        <line x1={681} y1={78} x2={714} y2={78} stroke="#555" strokeWidth="1.8" markerEnd="url(#ca-blk)"/>

        {/* Outer plant G1 */}
        <rect x={714} y={62} width={72} height={32} rx={3} fill="#f5d0ff" stroke="#9b59b6" strokeWidth="1.5"/>
        <text x={750} y={76} textAnchor="middle" fontSize={11} fill="#9b59b6" fontWeight="700">G₁(s)</text>
        <text x={750} y={87} textAnchor="middle" fontSize={8}  fill="#555">outer plant</text>

        {/* Y output */}
        <line x1={786} y1={78} x2={882} y2={78} stroke="#27ae60" strokeWidth="2" markerEnd="url(#ca-grn)"/>
        <circle cx={840} cy={78} r={4} fill="#27ae60"/>
        <text x={800} y={70} fontSize={10} fill="#27ae60" fontWeight="700">Y₁(s)</text>

        {/* Outer feedback */}
        <line x1={840} y1={82} x2={840} y2={160} stroke="#2E75B6" strokeWidth="1.5"/>
        <line x1={840} y1={160} x2={118} y2={160} stroke="#2E75B6" strokeWidth="1.5"/>
        <line x1={118} y1={160} x2={118} y2={92} stroke="#2E75B6" strokeWidth="1.5" markerEnd="url(#ca-blu)"/>
        <text x={480} y={172} textAnchor="middle" fontSize={9} fill="#2E75B6" fontStyle="italic">Outer feedback y₁ → outer summing junction</text>

        {/* ── Bottom: Architecture comparison table ── */}
        <rect x={10} y={250} width={880} height={122} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={450} y={268} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Architecture Comparison — Single Loop vs Cascade vs Cascade+FF</text>

        {/* Header */}
        <rect x={10} y={274} width={880} height={22} fill="#c0392b" rx={2}/>
        {['Feature','Single-Loop FB','Cascade','Cascade + Feedforward'].map((h,i) => (
          <text key={i} x={[18,160,340,560][i]} y={289} fontSize={10} fill="#fff" fontWeight="700">{h}</text>
        ))}
        {[
          ['Disturbance rejection','Slow — waits for output error', 'Fast inner-loop rejection','Proactive — cancels before error'],
          ['Reference tracking',   'Limited by stability margins', 'Improved bandwidth possible','Feedforward decouples tracking/stability'],
          ['Complexity',           'Simple, 1 controller, 1 sensor','2 controllers, 2 sensors',  '2+ controllers, sensors + FF model'],
          ['Model dependency',     'Low — feedback is robust',     'Medium — inner plant approx','High — FF requires accurate model'],
          ['Disturbance types',    'Any measurable via y output',  'Inner disturbance handled',  'Both inner/outer disturbances handled'],
        ].map((row, i) => (
          <g key={i}>
            <rect x={10} y={296+i*16} width={880} height={16} fill={i%2===0?'#fff5f5':'#fff'}/>
            <text x={18}  y={308+i*16} fontSize={8.5} fill="#7B0D0D" fontWeight="700">{row[0]}</text>
            <text x={160} y={308+i*16} fontSize={8.5} fill="#555">{row[1]}</text>
            <text x={340} y={308+i*16} fontSize={8.5} fill="#2E75B6">{row[2]}</text>
            <text x={560} y={308+i*16} fontSize={8.5} fill="#27ae60">{row[3]}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The full combined architecture integrates cascade control (inner/outer loops) with both reference feedforward and disturbance feedforward for both inner and outer disturbances. This is the highest-performance practical architecture: cascade handles the measurement of intermediate variables and fast inner-loop disturbances, while feedforward proactively cancels measured disturbances before they cause errors. The cost is complexity — more sensors, models, and tuning parameters.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 8 — Practical Examples & Performance Comparison
// ─────────────────────────────────────────────────────────────────
function DiagramPracticalExamples() {
  const W = 900, H = 400;
  const dt=0.02, tMax=20;

  // Simulate three scenarios for a temperature control example
  // Scenario: disturbance at t=5, step ref change at t=12
  const dist2 = (t) => (t >= 5 && t < 10) ? 1.2 : 0;
  const ref2   = (t) => t < 12 ? 1 : 1.5;

  const singleRef = simSingleLoop(2.5, 0.6, 0.3, tMax, dt, (t)=>dist2(t));
  const cascRef   = simCascade(2, 0.5, 0.15, 10, 1, tMax, dt, (t)=>dist2(t));
  const ffRef     = simFeedforward(2.5, 0.6, 0.5, tMax, dt, (t)=>dist2(t), -0.9);

  // Scale reference to match step change
  const adjustedRef = singleRef.map(p => ({ ...p, y: p.y * ref2(p.t) }));

  const PW=450, PH=240;
  const ox=18, oy=24;
  const sx = t  => ox+34+(t/tMax)*(PW-50);
  const sy = yv => oy+PH-22-Math.max(Math.min(yv,2.0),-0.2)*(PH-38)/2.2;

  const refPath    = singleRef.map(({t},i) => `${i===0?'M':'L'}${sx(t).toFixed(1)},${sy(ref2(t)).toFixed(1)}`).join(' ');
  const singlePath = singleRef.map(({t,y},i) => `${i===0?'M':'L'}${sx(t).toFixed(1)},${sy(y).toFixed(1)}`).join(' ');
  const cascPath   = cascRef.map(({t,y},i)   => `${i===0?'M':'L'}${sx(t).toFixed(1)},${sy(y).toFixed(1)}`).join(' ');
  const ffPath     = ffRef.map(({t,y},i)     => `${i===0?'M':'L'}${sx(t).toFixed(1)},${sy(y).toFixed(1)}`).join(' ');

  // Performance metrics
  const metrics = [
    { label:'Single-loop PI', color:'#c0392b' },
    { label:'Cascade',        color:'#2E75B6' },
    { label:'Feedforward+FB', color:'#27ae60' },
  ];

  const computeMetrics = (pts, refFn) => {
    let maxDev=0, iae=0, settleTime=tMax;
    for (let i=1; i<pts.length; i++) {
      const err = Math.abs(pts[i].y - refFn(pts[i].t));
      iae += err * dt;
      if (err > maxDev && pts[i].t > 4 && pts[i].t < 11) maxDev = err;
      if (pts[i].t > 12 && err < 0.05 && settleTime === tMax) settleTime = pts[i].t - 12;
    }
    return { maxDev: maxDev.toFixed(3), iae: iae.toFixed(2), settleTime: settleTime.toFixed(1) };
  };

  const m1 = computeMetrics(singleRef, ref2);
  const m2 = computeMetrics(cascRef,   ref2);
  const m3 = computeMetrics(ffRef,     ref2);
  const allMetrics = [m1, m2, m3];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Practical Performance Comparison — Temperature Control with Disturbance &amp; Setpoint Change</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <Arr id="pe-blk" color="#aaa"/>
          <clipPath id="pe-clip"><rect x={52} y={26} width={PW-46} height={PH-20}/></clipPath>
        </defs>

        {/* Response plot */}
        <rect x={ox} y={oy} width={PW} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={ox+PW/2} y={oy-8} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Simulated Temperature Control Response</text>

        {/* Disturbance shading */}
        <rect x={sx(5)} y={oy+6} width={sx(10)-sx(5)} height={PH-24} fill="#e07b00" opacity="0.05"/>
        <text x={(sx(5)+sx(10))/2} y={oy+18} textAnchor="middle" fontSize={8.5} fill="#e07b00" fontWeight="700">D active (t=5→10)</text>

        {/* Ref change */}
        <line x1={sx(12)} y1={oy+6} x2={sx(12)} y2={oy+PH-20} stroke="#555" strokeWidth="1.2" strokeDasharray="4,3" opacity="0.5"/>
        <text x={sx(12)+4} y={oy+18} fontSize={8.5} fill="#555">SP step t=12</text>

        {/* Grid */}
        {[2,4,6,8,10,12,14,16,18,20].map(tv=>(
          <line key={tv} x1={sx(tv)} y1={oy+6} x2={sx(tv)} y2={oy+PH-20} stroke="#f8f8f8" strokeWidth="1"/>
        ))}
        {[0.5,1.0,1.5].map(yv=>(
          <g key={yv}>
            <line x1={ox+32} y1={sy(yv)} x2={ox+PW-14} y2={sy(yv)} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={ox+28} y={sy(yv)+3} textAnchor="end" fontSize={8} fill="#bbb">{yv.toFixed(1)}</text>
          </g>
        ))}

        <line x1={ox+32} y1={oy+PH-20} x2={ox+PW-8}  y2={oy+PH-20} stroke="#aaa" strokeWidth="1.2" markerEnd="url(#pe-blk)"/>
        <line x1={ox+32} y1={oy+PH-20} x2={ox+32}    y2={oy+4}     stroke="#aaa" strokeWidth="1.2" markerEnd="url(#pe-blk)"/>
        <text x={ox+PW-4} y={oy+PH-16} fontSize={8} fill="#aaa">t</text>

        <path d={refPath}    fill="none" stroke="#aaa" strokeWidth="1.5" strokeDasharray="6,3" clipPath="url(#pe-clip)"/>
        <path d={singlePath} fill="none" stroke="#c0392b" strokeWidth="2.2" clipPath="url(#pe-clip)"/>
        <path d={cascPath}   fill="none" stroke="#2E75B6" strokeWidth="2.2" clipPath="url(#pe-clip)"/>
        <path d={ffPath}     fill="none" stroke="#27ae60" strokeWidth="2.2" clipPath="url(#pe-clip)"/>

        {/* Legend */}
        {[
          {c:'#aaa',    d:'5,3',lbl:'Reference r(t)'},
          {c:'#c0392b', d:'none',lbl:'Single-loop PI'},
          {c:'#2E75B6', d:'none',lbl:'Cascade'},
          {c:'#27ae60', d:'none',lbl:'Feedforward+FB'},
        ].map(({c,d,lbl},i) => (
          <g key={i}>
            <line x1={ox+36+i*106} y1={oy+PH-10} x2={ox+54+i*106} y2={oy+PH-10} stroke={c} strokeWidth="2" strokeDasharray={d==='none'?undefined:d}/>
            <text x={ox+58+i*106} y={oy+PH-6} fontSize={8.5} fill={c}>{lbl}</text>
          </g>
        ))}

        {/* ── RIGHT: Metrics + examples ── */}
        <rect x={484} y={oy} width={406} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={687} y={oy+18} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Performance Metrics (computed)</text>

        <rect x={492} y={oy+26} width={390} height={22} rx={2} fill="#c0392b"/>
        {['Controller','Max Dev','IAE','Settle Ts'].map((h,i) => (
          <text key={i} x={[500,600,670,750][i]} y={oy+41} fontSize={9.5} fill="#fff" fontWeight="700">{h}</text>
        ))}
        {metrics.map(({ label, color }, i) => {
          const m = allMetrics[i];
          return (
            <g key={i}>
              <rect x={492} y={oy+48+i*24} width={390} height={24} fill={i%2===0?'#fafafa':'#fff'}/>
              <text x={500} y={oy+64+i*24} fontSize={9.5} fill={color} fontWeight="700">{label}</text>
              <text x={600} y={oy+64+i*24} fontSize={9}   fill="#333" fontFamily="'Courier New',monospace">{m.maxDev}</text>
              <text x={670} y={oy+64+i*24} fontSize={9}   fill="#333" fontFamily="'Courier New',monospace">{m.iae}</text>
              <text x={750} y={oy+64+i*24} fontSize={9}   fill="#333" fontFamily="'Courier New',monospace">{m.settleTime}s</text>
            </g>
          );
        })}

        {/* Real-world examples */}
        <rect x={492} y={oy+126} width={390} height={110} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.2"/>
        <text x={687} y={oy+140} textAnchor="middle" fontSize={10} fill="#7B0D0D" fontWeight="700">Industrial Examples</text>
        {[
          ['Cascade:','Boiler: outer=temperature, inner=fuel flow rate'],
          ['Cascade:','Refinery: outer=column temp, inner=reflux flow'],
          ['Cascade:','Robot arm: outer=position, inner=motor current'],
          ['Cascade:','Chemical: outer=reactor temp, inner=coolant flow'],
          ['FF:','Feedwater control: measure steam flow, pre-adjust feed'],
          ['FF:','HVAC: outdoor temp sensor → preemptive heating/cooling'],
          ['Combined:','Aircraft pitch: outer=attitude, inner=rate, FF=pilot cmd'],
        ].map((row, j) => (
          <g key={j}>
            <text x={500} y={oy+155+j*12} fontSize={8.5} fill="#7B0D0D" fontWeight="700">{row[0]}</text>
            <text x={560} y={oy+155+j*12} fontSize={8.5} fill="#555">{row[1]}</text>
          </g>
        ))}
      </svg>

      {/* Bottom: When to use each strategy */}
      <svg viewBox="0 0 900 130" style={{ width:'100%', display:'block', marginTop:'-4px' }}>
        <rect x={10} y={8} width={880} height={118} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={450} y={24} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">When to Use Each Strategy</text>
        {[
          { x:18,  color:'#c0392b', bg:'#FEF0F0', title:'Use Cascade when:',
            lines:['Intermediate variable is measurable','Inner dynamics are at least 3× faster','Inner disturbances are the main problem'] },
          { x:236, color:'#2E75B6', bg:'#eaf3ff', title:'Use Reference FF when:',
            lines:['Fast reference tracking is required','FB bandwidth is stability-limited','Plant model is well known (G(s) available)'] },
          { x:454, color:'#27ae60', bg:'#f0fff4', title:'Use Disturbance FF when:',
            lines:['Disturbance is measurable at source','Disturbance reaches output before feedback','FB loop too slow to reject disturbance'] },
          { x:672, color:'#9b59b6', bg:'#f5d0ff', title:'Combine all when:',
            lines:['Tight specs on both tracking & rejection','Multiple disturbance sources exist','Plant dynamics are well characterised'] },
        ].map(({ x, color, bg, title, lines }) => (
          <g key={x}>
            <rect x={x+2} y={32} width={212} height={88} rx={3} fill={bg} stroke={color} strokeWidth="1.2"/>
            <text x={x+10} y={46} fontSize={10} fill={color} fontWeight="700">{title}</text>
            {lines.map((l,j) => (
              <text key={j} x={x+10} y={60+j*16} fontSize={9} fill="#333">▸ {l}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>A simulated temperature control scenario with both an input disturbance (t=5→10) and a setpoint step (t=12) shows the three strategies side-by-side. The performance metric table quantifies the improvement: cascade reduces peak disturbance deviation, and feedforward+FB reduces both peak deviation and IAE. The industrial examples and decision guide at the bottom help practitioners select the right architecture for their specific application.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main Export
// ─────────────────────────────────────────────────────────────────
export default function CascadeFeedforward() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Control Systems — Cascade &amp; Feedforward Control</h1>
      <p style={S.p}>
        Single-loop PID feedback control is the workhorse of industrial automation, but it has fundamental limitations: it can only react after an error has appeared at the measured output, and its bandwidth is constrained by the stability margins of the entire plant. Cascade control and feedforward control are two complementary architectural strategies that overcome these limitations — cascade by introducing a faster inner feedback loop that corrects disturbances before they propagate to the primary output, and feedforward by using advance knowledge of the reference or disturbance to proactively cancel their effects before errors occur. Together, these techniques underpin the majority of high-performance process control systems in refineries, power plants, chemical reactors, and precision motion control.
      </p>

      <InfoBox title="Core Concepts at a Glance" items={[
        "Cascade: inner loop (fast) inside outer loop (slow) — inner corrects disturbances before they reach the outer output",
        "Inner loop bandwidth must be 3–10× outer loop bandwidth for cascade to provide benefit",
        "Tune cascade sequentially: inner loop first (open outer), then outer loop (with inner closed)",
        "Reference feedforward: F_r(s) = 1/G(s) — invert plant to cancel dynamics and track reference instantly",
        "Disturbance feedforward: F_d(s) = −G_d(s)/G(s) — measure disturbance, compute cancellation signal",
        "Feedforward does NOT affect closed-loop stability — it is open-loop action added to the FB controller",
        "Feedforward alone is fragile (model-dependent); always combine with feedback for robustness",
        "Combined cascade + FF achieves best of both: fast inner rejection + proactive disturbance cancellation",
      ]}/>

      <h2 style={S.h2}>Cascade Control Structure</h2>
      <p style={S.p}>
        Cascade control nests two feedback loops: an inner (secondary) loop with a fast controller C₂ driving an inner plant G₂, and an outer (primary) loop with a slower controller C₁ commanding the inner setpoint r₂. The outer controller sees the inner loop as a single "fast plant" — the inner closed-loop transfer function. Inner disturbances entering G₂ are corrected by C₂ before they propagate through G₁ to the primary output y₁.
      </p>
      <DiagramCascadeStructure/>
      <Table2 rows={[
        ["Cascade block diagram","R(s) → C₁(s) → r₂(s) → [inner loop: C₂, G₂] → y₂ → G₁(s) → y₁\nOuter controller C₁ sets inner setpoint r₂\nInner controller C₂ drives actuator to follow r₂"],
        ["Inner (secondary) loop","Controls fast/intermediate variable y₂ (e.g. flow, current)\nFast response: typically P or PI only\nMust be stable with good disturbance rejection independently"],
        ["Outer (primary) loop","Controls main process variable y₁ (e.g. temperature, position)\nSees inner CL TF as the 'plant': G_inner_CL(s)\nSlower — typically PI or PID"],
        ["Inner CL transfer function","G_CL,inner = C₂G₂/(1+C₂G₂)\nFor P-only C₂=Kp, G₂=1/(τ₂s+1): G_CL = Kp/(τ₂s+1+Kp) = 1/(τ₂/(1+Kp)·s+1/(1+Kp))\nHigher inner Kp → faster effective inner TF, closer to unity gain"],
        ["Cascade closed-loop TF","T(s) = C₁·G_CL,inner·G₁ / (1 + C₁·G_CL,inner·G₁)\nOuter loop treats the inner as a fast 'virtual plant'\nAllows outer PI to use higher bandwidth without destabilising inner"],
        ["Disturbance TF (inner D₂)","y₁/D₂ = G₁ / (1 + C₁·G_CL,inner·G₁) × G_CL,inner\nFaster inner loop → smaller y₁/D₂ at all frequencies\nKey benefit: inner Kp directly reduces D₂'s effect on y₁"],
        ["Bandwidth separation rule","ω_BW,inner ≥ 3×ω_BW,outer  (minimum)\nω_BW,inner ≥ 10×ω_BW,outer  (recommended)\nIf inner too slow: no benefit; if too fast: noise problems"],
        ["Measurement requirements","Inner variable y₂ must be measurable (additional sensor needed)\nInner variable must be physically between actuator and outer output\nMust have significant dynamics (otherwise no cascade benefit)"],
      ]}/>

      <h2 style={S.h2}>Cascade vs Single-Loop: Disturbance Rejection</h2>
      <p style={S.p}>
        The primary motivation for cascade control is faster and more effective rejection of inner-loop disturbances. When a disturbance enters the inner plant (for example a supply pressure fluctuation disturbing a flow valve), the single-loop controller can only react after the disturbance has propagated all the way through both plants to affect y₁. The cascade inner loop detects the disturbance through y₂ and corrects it locally — often before y₁ has moved at all.
      </p>
      <DiagramCascadeVsSingle/>
      <Table2 rows={[
        ["Inner disturbance path","D₂ → G₂ → y₂  (fast path, corrected by inner loop)\nD₂ → G₂ → G₁ → y₁  (slow path, cascade blocks this early)"],
        ["Single-loop response to D₂","Disturbance must propagate through entire plant before y₁ deviates\nController only reacts after y₁ error appears — inherent delay\nMaximum deviation proportional to G₁·G₂·D₂ magnitude"],
        ["Cascade response to D₂","Inner loop senses y₂ change immediately\nC₂ counteracts D₂ within inner loop bandwidth\nOnly small residual propagates to y₁ if inner loop is fast enough"],
        ["Peak deviation improvement","Improvement factor ≈ 1 + C₂(0)G₂(0) at DC\nFor inner Kp=10, G₂(0)=1: y₁ peak ≈ 1/11 of single-loop\nHigher inner Kp → smaller outer disturbance effect"],
        ["Outer disturbance D₁","Enters after inner loop — both single and cascade respond identically\nCascade offers no benefit for outer-loop disturbances\nOnly cascade + disturbance FF addresses D₁ proactively"],
        ["Load disturbance scenario","Motor speed control: single-loop slow to recover from load torque step\nCascade inner current loop: current increases instantly to maintain torque\nOuter speed recovers in fraction of the time"],
        ["Simulation findings","Cascade typically reduces inner disturbance peak by 5–20×\nIAE (integral of absolute error) also significantly reduced\nSettling time after disturbance shorter by 3–8×"],
        ["When cascade is not beneficial","Outer disturbances only (no inner variable to measure)\nInner dynamics not significantly faster than outer\nInner variable doesn't exist or isn't measurable\nPlant gain not sufficient to allow inner Kp high enough"],
      ]}/>

      <h2 style={S.h2}>Inner-Loop Bandwidth &amp; Cascade Tuning</h2>
      <p style={S.p}>
        The speed of the inner loop directly determines the benefit of cascade over single-loop control. As inner-loop gain increases, the effective inner plant time constant seen by the outer loop shrinks — allowing the outer controller to use a higher bandwidth and achieve faster tracking. However, there are practical limits: too high an inner gain amplifies measurement noise and may excite unmodelled fast dynamics.
      </p>
      <DiagramInnerLoopEffect/>
      <Table2 rows={[
        ["Cascade tuning sequence","Step 1: Put outer controller in manual (open outer loop)\nStep 2: Tune inner controller for fast stable response with ≤10% OS\nStep 3: Verify inner loop stable independently\nStep 4: Close inner loop; tune outer controller (sees fast inner plant)"],
        ["Inner controller choice","P only: simplest; if inner variable has no steady-state requirement\nPI: needed if inner variable must track exactly (e.g. flow control)\nAvoid D in inner loop: fast inner + D → noise amplification"],
        ["Outer controller choice","PI: most common for outer loop in industrial cascade\nPID: if outer plant has significant dead time or is second-order\nTune Kp_outer lower than for single-loop (inner already handles fast dynamics)"],
        ["Inner loop P-gain limit","Too high Kp_inner: sensor noise → actuator chatter, wear\nToo high Kp_inner: excites unmodelled fast resonances\nRule: Kp_inner ≤ Ku_inner / 3  (one-third of inner ultimate gain)"],
        ["Effective plant for outer","G_effective(s) = G_CL,inner(s) × G₁(s)\nG_CL,inner ≈ 1/(1+1/Kp_inner) ≈ 1  for large Kp_inner\nOuter controller designs against near-unity inner × G₁(s)"],
        ["Anti-windup in cascade","Both controllers need anti-windup\nOuter controller: r₂ may be constrained by inner saturation\nInner controller: actuator saturation applies to inner directly\nCondition outer anti-windup on inner output"],
        ["Retuning after cascade","Outer loop must be retuned after changing inner gains\nChanging inner Kp changes effective plant for outer loop\nUse SIMC or ZN on G_effective for outer tuning after inner is fixed"],
        ["Cascade with dead time","Inner plant dead time L₂: inner loop cannot react until after L₂\nOuter plant dead time L₁: outer loop further limited\nSmith predictor in inner loop can help compensate L₂"],
      ]}/>

      <h2 style={S.h2}>Feedforward Control Structure</h2>
      <p style={S.p}>
        Feedforward control acts open-loop: it uses a model of the plant (or disturbance path) to compute the control action needed before any error occurs. Reference feedforward uses the setpoint signal directly, computing how much input is needed to produce the desired output. Disturbance feedforward measures the disturbance at its source and injects a cancellation signal into the control path.
      </p>
      <DiagramFeedforwardStructure/>
      <Table2 rows={[
        ["Reference FF principle","U_FF(s) = F_r(s)·R(s)\nF_r(s) = 1/G(s) ideally: G(s)·F_r(s)·R(s) = R(s) exactly\nOutput tracks reference without waiting for error — proactive"],
        ["Disturbance FF principle","Measure D(s) at source before it enters the plant\nU_FF_d(s) = F_d(s)·D(s)\nF_d(s) = −G_d(s)/G(s): cancels disturbance effect at output"],
        ["Combined FF + FB","U(s) = C(s)·E(s) + F_r(s)·R(s) + F_d(s)·D(s)\nFB handles modelling errors, unmeasured disturbances, plant variations\nFF handles the predictable, measurable part of the control demand"],
        ["Stability independence","Feedforward is open-loop → does not create new feedback paths\nAdding FF does not change closed-loop characteristic equation\nStability analysis of FB loop applies unchanged\nFF gain errors appear as offset, not instability"],
        ["Ideal FF impropriety","G(s)=K/(τs+1): F_r=1/G=(τs+1)/K — has a zero (improper TF)\nImpulse response: impulsive input required for perfect tracking\nIn practice: add low-pass filter F_r_prac = (τs+1)/(K·(τ_f·s+1)^n)\nChoose τ_f ≪ τ for near-perfect tracking with bounded input"],
        ["Static (DC) feedforward","Simplest: F_r = 1/G(0) = constant gain\nEliminates steady-state error from reference\nNo dynamics needed — just a gain in the forward path\nUseful for slow plants where dynamic cancellation impractical"],
        ["Disturbance path model","G_d(s) = transfer function from disturbance to output\nMust be identified separately from G(s)\nOften G_d = G for input disturbances at the plant input\nOutput disturbances: G_d = 1 (direct additive)"],
        ["Two-degree-of-freedom (2DOF)","FF on reference + FB on error is a 2DOF controller\nAllows independent tuning of reference tracking and disturbance rejection\nFB gains for disturbance rejection; FF for reference tracking speed"],
      ]}/>

      <h2 style={S.h2}>Feedforward Performance: Ideal, Imperfect, and Combined</h2>
      <p style={S.p}>
        Perfect feedforward is theoretically achievable but practically impossible — it requires an exact plant model and often an improper transfer function. In practice, feedforward is always imperfect: gain mismatch, unmodelled dynamics, or time delays reduce its effectiveness. The key insight is that combining imperfect feedforward with feedback produces a system that is better than either alone: feedforward handles the bulk of the correction, and feedback cleans up the residual error.
      </p>
      <DiagramFFComparison/>
      <Table2 rows={[
        ["Perfect FF (theoretical)","F_d = −G_d/G exactly: disturbance has zero effect on output\nRequires exact knowledge of G(s) and G_d(s)\nAchievable in simulation; not practical due to modelling errors"],
        ["Gain mismatch effect","If F_d = −(1−δ)·G_d/G: residual error = δ·S(s)·D(s)\nδ = fractional model error\nResidual handled by feedback loop — smaller than without FF"],
        ["Time delay mismatch","If process has dead time L not in FF model: FF leads/lags\nEarly FF: counteracts D before it arrives (beneficial)\nLate FF: partial cancellation; FB handles remainder"],
        ["Imperfect FF + FB","Practical optimal: F_d = −0.7 to −0.9 times ideal\nFF eliminates 70–90% of disturbance effect\nFB corrects remaining 10–30% — much smaller burden\nResult better than FB alone, even with imperfect model"],
        ["Reference FF improvement","Even partial FF (FF gain < 1/G) improves tracking speed\nStep reference: initial rise faster without increasing FB gain\nAvoids the transient tradeoff between tracking speed and stability"],
        ["FF sensitivity analysis","∂y/∂ΔF_d = G(s): output error proportional to FF error × plant gain\nMore sensitive for high-gain plants\nTest by varying FF gain ±20% — check output response sensitivity"],
        ["Online FF adaptation","Measure residual disturbance effect after FF action\nAdjust F_d gain to minimize residual using gradient descent\nAdaptive FF: F_d updated online — self-improving with experience"],
        ["Physical implementation","FF computed digitally: subtract from control signal before DAC\nFor analogue implementation: summing amplifier adds FF and FB outputs\nCare needed for saturation: total u = u_FB + u_FF must stay within limits"],
      ]}/>

      <h2 style={S.h2}>Feedforward Compensator Design</h2>
      <p style={S.p}>
        Designing a feedforward compensator requires: (1) a model of the plant G(s) and/or disturbance path G_d(s), (2) a choice of approximation level (static gain only, first-order dynamic, full inversion), and (3) a stability check ensuring the compensator can be physically implemented. The frequency-domain view shows exactly how the FF compensator reshapes the loop characteristics.
      </p>
      <DiagramFFFrequency/>
      <Table2 rows={[
        ["Ideal reference FF design","F_r_ideal(s) = 1/G(s)\nRequires plant model — identified from step test or frequency sweep\nIf G has RHP zeros: 1/G has RHP poles (unstable FF) — minimum-phase plants only"],
        ["Practical (filtered) FF","Add filter: F_r_prac(s) = G⁻¹(s) × 1/(τ_f·s+1)ⁿ\nChoose τ_f = 0.01–0.1 × dominant plant time constant\nMake proper: n ≥ (poles of G) − (zeros of G)"],
        ["Ideal disturbance FF","F_d(s) = −G_d(s)/G(s)\nIf G_d = G (input disturbance): F_d = −1 (simple gain!)\nRequires models of both disturbance path and plant"],
        ["Static FF gain only","F_r = 1/G(0) = 1/K_process\nF_d = −G_d(0)/G(0)\nSimplest implementation; eliminates only DC tracking/disturbance error\nGood starting point; add dynamics if performance insufficient"],
        ["Minimum-phase plants","Plants with no RHP zeros: exact inversion is stable\nNon-minimum-phase: 1/G has unstable poles → cannot invert exactly\nApproximate: invert only stable part; leave RHP zeros uncancelled"],
        ["Dead-time compensation","Plant with delay: G(s) = G₀(s)·e^(−Ls)\nFF: F_r(s) = 1/(G₀(s)·e^(−Ls)) — requires future reference knowledge\nPractical: use preview (look-ahead buffer) for L seconds\nOr: Smith predictor modifies FB loop to remove apparent dead time"],
        ["FF for time-varying plants","If plant gain K changes: static FF must be updated\nSchedule FF gain with operating condition (e.g. flow rate, temperature)\nAdaptive FF: continuously estimate G(0) and adjust F_r accordingly"],
        ["Verification","Simulate: apply step D with FF on and off — compare y(t)\nFrequency sweep: measure |y/D| with and without FF\nTarget: |y/D|_FF ≪ |y/D|_no_FF at disturbance frequencies"],
      ]}/>

      <h2 style={S.h2}>Combined Cascade + Feedforward Architecture</h2>
      <p style={S.p}>
        The most powerful practical control architecture combines cascade (for inner-loop disturbance rejection and bandwidth augmentation) with feedforward (for proactive cancellation of measurable disturbances and fast reference tracking). This architecture is standard in high-performance industrial processes — petroleum refineries, paper mills, aircraft flight control, and semiconductor manufacturing.
      </p>
      <DiagramCombinedArchitecture/>
      <Table2 rows={[
        ["Full architecture","R(s) → [ref FF F_r + outer FB C₁] → r₂(s)\nr₂ → [inner FB C₂] → u → [D₂ summing] → G₂ → y₂\ny₂ → G₁ → [D₁ summing] → y₁\nD₂ FF: F_d2 injects into inner summing\nD₁ FF: F_d1 injects into outer summing"],
        ["Signal flow summary","u = C₂(r₂−y₂) + F_d2·D₂  (inner actuator signal)\nr₂ = C₁(R−y₁) + F_r·R       (inner setpoint)\ny₁ affected by: tracking, D₁, D₂ — all partially handled"],
        ["Tuning cascade+FF","Tune inner loop first (most important)\nAdd D₂ FF: step D₂ with/without FF, adjust F_d2 gain\nTune outer loop with inner closed\nAdd ref FF: compare tracking with/without, adjust F_r\nAdd D₁ FF last; verify combined stability"],
        ["Benefits vs single-loop","Reference tracking: faster (FF), zero SS error (integral FB)\nInner disturbance: fast correction (inner loop + D₂ FF)\nOuter disturbance: FB + D₁ FF (proactive)\nAll benefits compound — substantially better IAE and peak deviation"],
        ["Cost vs single-loop","2 sensors (y₁, y₂) instead of 1\n2 controllers (C₁, C₂) to tune\nDisturbance sensors for FF (may need additional hardware)\nPlant models required for FF design\nMore complex failure analysis"],
        ["Partial implementations","Cascade only (no FF): add when inner variable measurable\nRef FF only: add when tracking speed is primary concern\nDisturbance FF only: add when specific disturbance is measurable\nPragmatic: add each piece only when it solves a real problem"],
        ["Robustness","Cascade FB: robust to inner plant uncertainty (inner loop corrects)\nRef FF: sensitive to model error (δG)\nDist FF: sensitive to model error (δG_d)\nRule: FF gain intentionally set <100% ideal to retain FB robustness"],
        ["Applications","Boiler control: pressure (outer) + flow (inner) + FF for steam demand\nDistillation: composition (outer) + temperature (inner) + FF for feed rate\nServomotor: position (outer) + velocity/current (inner) + FF for trajectory"],
      ]}/>

      <h2 style={S.h2}>Practical Performance &amp; Industrial Examples</h2>
      <p style={S.p}>
        The practical value of cascade and feedforward control is best understood through quantitative comparison on realistic scenarios. The simulation below uses a common industrial benchmark: a process with an inner-loop disturbance and a setpoint step change, measuring peak deviation, integral absolute error (IAE), and settling time for three architectures.
      </p>
      <DiagramPracticalExamples/>
      <Table2 rows={[
        ["Temperature control (boiler)","Outer: steam temperature (slow, thermal); Inner: fuel gas flow (fast)\nD₂: supply pressure → fuel flow perturbation\nCascade inner loop corrects fuel flow instantly; outer temp barely changes"],
        ["Flow control (valve cascade)","Outer: liquid level; Inner: flow rate through valve\nInner: overcomes valve friction, hysteresis, supply pressure variations\nRef FF: flow demand from level controller anticipates needed flow change"],
        ["Position control (servo)","Outer: shaft position; Inner: motor current (torque)\nD₂: load torque disturbance (instantaneous current change sensed)\nRef FF: precomputed torque profile for known motion trajectory"],
        ["Aircraft pitch control","Outer: pitch angle θ; Inner: pitch rate q\nD₂: atmospheric turbulence (rate sensor responds instantly)\nRef FF: pilot stick command fed forward to bypass rate loop delays"],
        ["Chemical reactor","Outer: product concentration; Inner: reactor temperature\nD₂: feed composition changes; D₁: cooling water temperature\nFF: feed flow rate (measurable) → preemptive temperature adjustment"],
        ["HVAC system","Outer: room temperature; Inner: air flow / valve position\nFF: outdoor temperature sensor → anticipate heating/cooling demand\nFF on occupancy schedule: pre-heat before workers arrive"],
        ["Paper machine","Outer: paper thickness (caliper); Inner: press nip pressure\nD₂: pulp consistency fluctuations\nFF: basis weight measurement → anticipate pressing requirements"],
        ["CNC machine tool","Outer: workpiece dimension; Inner: axis velocity/force\nRef FF: precomputed axis trajectory with dynamic model\nFF: cutting force measurement → preemptive motor torque adjustment"],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "Cascade inner CL TF: G_CL,inner = C₂G₂/(1+C₂G₂)  |  For P: τ_eff = τ₂/(1+Kp), K_eff ≈ 1",
        "Cascade outer CL TF: T = C₁·G_CL,inner·G₁ / (1 + C₁·G_CL,inner·G₁)",
        "Bandwidth rule: ω_BW,inner ≥ 3–10 × ω_BW,outer",
        "Reference FF: F_r(s) = 1/G(s)  |  Practical: add filter poles for proper TF",
        "Disturbance FF: F_d(s) = −G_d(s)/G(s)  |  Static: F_d = −G_d(0)/G(0)",
        "Combined: U(s) = C(s)E(s) + F_r(s)R(s) + F_d(s)D(s)",
        "FF does not affect stability: characteristic eqn = 1+G(s)C(s) = 0 (unchanged)",
        "Two-DOF: FB tunes disturbance rejection; FF tunes reference tracking — independently",
      ]}/>
    </div>
  );
}